import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import stateService from '../../services/states';
import leadsService from '../../services/leads';
import leadPurchasesService from '../../services/leadPurchases';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Spin, Modal, message, Select, Row, Col, Input,  Tag, DatePicker, Typography, Card, Space, Upload, Button, Table, Badge, Checkbox, Divider, Segmented, Tour, Steps, Dropdown, Alert, Statistic } from 'antd';
import { UploadOutlined, SearchOutlined, LockOutlined, EyeOutlined, DeleteOutlined, ShoppingCartOutlined, CloseOutlined, EnvironmentOutlined, CalendarOutlined, DollarOutlined, InsuranceOutlined, ClockCircleOutlined, SettingOutlined, QuestionCircleOutlined, PrinterOutlined, MailOutlined, PhoneOutlined, TagOutlined, FileTextOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import '../../styles/tables.css';
import '../../styles/filter-tags.css';
import { useCart } from '../../contexts/CartContext';
import Cart from '../../components/Cart';
import { useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import PurchaseVerificationPrint from '../../components/PurchaseVerificationPrint';
import axios from 'axios';

const { Option } = Select;
const { RangePicker } = DatePicker;

 const stripePromise = loadStripe('pk_live_51PiqT2LYA1pVnjq6i2lGHznNj4x71ty0VdaJFoPVD3L6CUYvAdq6xrK7sAYTmhJXIO0Qyzj3VlpfD67fGjatEmB700vcbR2RTz');
//const stripePromise = loadStripe('pk_test_r3CWfIV9ITtJWo4wD8EWdPtH');

const PurchaseModal = ({ visible, onClose, record, onPurchase, isLoading }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isCardComplete, setIsCardComplete] = React.useState(false);
  const [isProcessing, setIsProcessing] = React.useState(false);

  const handleConfirmPurchase = async () => {
    if (!record || !isCardComplete) {
      message.error('Please complete card details before purchase.');
      return;
    }

    setIsProcessing(true);
    try {
      const cardElement = elements.getElement(CardElement);
      const { paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      await onPurchase(record.id, paymentMethod.id);
    } catch (error) {
      message.error('Payment failed: ' + error.message);
      setIsProcessing(false);
    }
  };

  const handleCardChange = (event) => {
    setIsCardComplete(event.complete);
  };

  React.useEffect(() => {
    if (!visible) {
      setIsProcessing(false);
    }
  }, [visible]);

  if (!record) {
    return null; // Do not render the modal if no record is provided
  }

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      onOk={handleConfirmPurchase}
      okText="Purchase"
      okButtonProps={{ 
        icon: <ShoppingCartOutlined />,
        loading: isLoading,
        disabled: !isCardComplete || isLoading || isProcessing
      }}
      confirmLoading={isLoading || isProcessing}
      cancelButtonProps={{ disabled: isLoading || isProcessing }}
    >
      <div>
        <Typography.Title type="link" level={5}>Are you sure you want to purchase this lead?</Typography.Title>
        <div style={{ marginTop: 16 }}>
          <Typography.Paragraph>
            <Typography.Text strong>Price: </Typography.Text>
            $5.00
          </Typography.Paragraph>
          <Typography.Paragraph>
            <Typography.Text strong>Name: </Typography.Text>
            {record?.first_name} {record?.last_name}
          </Typography.Paragraph>
          <Typography.Paragraph>
            <Typography.Text strong>State: </Typography.Text>
            {record?.state_name}
          </Typography.Paragraph>
        </div>
        {isLoading && (
          <div style={{ marginBottom: 16, textAlign: 'center' }}>
            <Spin />
            <p style={{ marginTop: 8 }}>Processing your payment...</p>
          </div>
        )}
        <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: 16 }}>
          <CardElement 
            disabled={isLoading}
            onChange={handleCardChange}
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#424770',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                },
                invalid: {
                  color: '#9e2146',
                },
              },
            }}
          />
        </div>
      </div>
    </Modal>
  );
};

// Reusable hook for column visibility
function useColumnVisibility(key, allColumns) {
  const storageKey = `column-visibility-${key}`;
  const [visibleKeys, setVisibleKeys] = React.useState(() => {
    const stored = localStorage.getItem(storageKey);
    if (stored) return JSON.parse(stored);
    return allColumns.map(col => col.key || col.dataIndex);
  });

  React.useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(visibleKeys));
  }, [visibleKeys, storageKey]);

  const setColumnVisibility = (key, visible) => {
    setVisibleKeys(prev => {
      if (visible) return Array.from(new Set([...prev, key]));
      return prev.filter(k => k !== key);
    });
  };

  const resetColumns = () => {
    setVisibleKeys(allColumns.map(col => col.key || col.dataIndex));
  };

  return {
    visibleKeys,
    setColumnVisibility,
    resetColumns,
    isVisible: (key) => visibleKeys.includes(key)
  };
}

const UploadErrorModal = ({ visible, onClose, errors, uploadSummary }) => {
  // Categorize errors
  const categorizedErrors = React.useMemo(() => {
    if (!errors || errors.length === 0) return { duplicates: [], validation: [], other: [] };
    
    const duplicates = [];
    const validation = [];
    const other = [];
    
    errors.forEach(error => {
      const reason = error.reason?.toLowerCase() || '';
      if (reason.includes('already exists') || reason.includes('duplicate')) {
        duplicates.push(error);
      } else if (reason.includes('format') || reason.includes('invalid') || reason.includes('missing')) {
        validation.push(error);
      } else {
        other.push(error);
      }
    });
    
    return { duplicates, validation, other };
  }, [errors]);

  const columns = [
    {
      title: 'Row',
      dataIndex: 'row',
      key: 'row',
      width: 80,
      fixed: 'left',
    },
    {
      title: 'Error Type',
      key: 'error_type',
      width: 120,
      render: (_, record) => {
        const reason = record.reason?.toLowerCase() || '';
        if (reason.includes('already exists') || reason.includes('duplicate')) {
          return <Tag color="orange">Duplicate</Tag>;
        } else if (reason.includes('format') || reason.includes('invalid')) {
          return <Tag color="red">Validation</Tag>;
        } else {
          return <Tag color="default">Other</Tag>;
        }
      },
    },
    {
      title: 'Error Details',
      dataIndex: 'reason',
      key: 'reason',
      width: 350,
      render: (reason, record) => (
        <div>
          <div style={{ color: '#ff4d4f', marginBottom: 8, fontWeight: 500 }}>{reason}</div>
          {record.problematic_fields && record.problematic_fields.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <strong style={{ fontSize: '12px' }}>Problematic Fields:</strong>
              <div style={{ marginTop: 4 }}>
                {record.problematic_fields.filter(f => f).map(field => (
                  <Tag key={field} color="red" style={{ marginRight: 4, marginBottom: 4 }}>
                    {field}
                  </Tag>
                ))}
              </div>
            </div>
          )}
          {record.data && (
            <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
              <strong>Record Data:</strong>
              <div style={{ marginTop: 4, maxHeight: '100px', overflowY: 'auto', background: '#f5f5f5', padding: '8px', borderRadius: '4px' }}>
                {Object.entries(record.data).slice(0, 5).map(([key, value]) => (
                  <div key={key} style={{ marginBottom: 2 }}>
                    <strong>{key}:</strong> {String(value || '-').substring(0, 50)}
                  </div>
                ))}
                {Object.keys(record.data).length > 5 && (
                  <div style={{ color: '#999', fontStyle: 'italic' }}>
                    ... and {Object.keys(record.data).length - 5} more fields
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Expected Format',
      key: 'expected_format',
      width: 200,
      render: (_, record) => {
        const reason = record.reason?.toLowerCase() || '';
        if (reason.includes('birth date')) {
          return <Typography.Text type="secondary" style={{ fontSize: '12px' }}>MM/DD/YYYY or MMM DD YYYY</Typography.Text>;
        }
        if (reason.includes('lead_old') || reason.includes('created')) {
          return <Typography.Text type="secondary" style={{ fontSize: '12px' }}>YYYY-MM-DD HH:mm:ss or flexible</Typography.Text>;
        }
        if (reason.includes('state')) {
          return <Typography.Text type="secondary" style={{ fontSize: '12px' }}>Valid US state name</Typography.Text>;
        }
        if (reason.includes('identifier')) {
          return <Typography.Text type="secondary" style={{ fontSize: '12px' }}>Unique identifier</Typography.Text>;
        }
        return null;
      },
    },
  ];

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
          <span>Upload Results & Skipped Records</span>
          {errors && errors.length > 0 && (
            <Tag color="red" style={{ marginLeft: 'auto' }}>
              {errors.length} skipped
            </Tag>
          )}
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={1000}
      footer={[
        <Button key="close" type="primary" onClick={onClose}>
          Close
        </Button>,
      ]}
    >
      {/* Summary Statistics */}
      {uploadSummary && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={8}>
            <Card size="small" style={{ textAlign: 'center', background: '#e6f7ff' }}>
              <Statistic
                title="Records Imported"
                value={uploadSummary.records_imported || 0}
                valueStyle={{ color: '#52c41a', fontSize: '24px' }}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small" style={{ textAlign: 'center', background: '#fff2f0' }}>
              <Statistic
                title="Records Skipped"
                value={uploadSummary.records_skipped || 0}
                valueStyle={{ color: '#ff4d4f', fontSize: '24px' }}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small" style={{ textAlign: 'center', background: '#fffbe6' }}>
              <Statistic
                title="Duplicates Found"
                value={categorizedErrors.duplicates.length}
                valueStyle={{ color: '#faad14', fontSize: '24px' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      <div style={{ marginBottom: 16 }}>
        <Alert
          message={
            uploadSummary?.records_imported > 0
              ? "Some records were successfully imported, but others were skipped due to errors"
              : "All records were skipped due to errors"
          }
          description={
            <div>
              <p>Please review the errors below and fix them in your CSV file before uploading again.</p>
              {categorizedErrors.duplicates.length > 0 && (
              <p style={{ marginTop: 8 }}>
                  <strong>Note:</strong> {categorizedErrors.duplicates.length} records were skipped because they already exist in the system (duplicate identifiers).
              </p>
              )}
            </div>
          }
          type={uploadSummary?.records_imported > 0 ? 'warning' : 'error'}
          showIcon
        />
      </div>

      {/* Error Categories Tabs */}
      {errors && errors.length > 0 && (
        <div>
          <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
            {categorizedErrors.duplicates.length > 0 && (
              <Tag color="orange" style={{ fontSize: '14px', padding: '4px 12px' }}>
                Duplicates: {categorizedErrors.duplicates.length}
              </Tag>
            )}
            {categorizedErrors.validation.length > 0 && (
              <Tag color="red" style={{ fontSize: '14px', padding: '4px 12px' }}>
                Validation Errors: {categorizedErrors.validation.length}
              </Tag>
            )}
            {categorizedErrors.other.length > 0 && (
              <Tag color="default" style={{ fontSize: '14px', padding: '4px 12px' }}>
                Other Errors: {categorizedErrors.other.length}
              </Tag>
            )}
          </div>
          
      <Table
        columns={columns}
        dataSource={errors}
            rowKey={(record, index) => `error-${record.row}-${index}`}
            pagination={{ 
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} skipped records`
            }}
            scroll={{ x: 800, y: 500 }}
        size="small"
            rowClassName={(record) => {
              const reason = record.reason?.toLowerCase() || '';
              if (reason.includes('already exists') || reason.includes('duplicate')) {
                return 'error-row-duplicate';
              }
              return 'error-row-validation';
            }}
          />
        </div>
      )}
    </Modal>
  );
};

const LeadsPage = () => {
  const queryClient = useQueryClient();
  const { currentUser } = useSelector((state) => state.user);
  const isAdmin = currentUser?.role === 'admin';
  const [selectedState, setSelectedState] = React.useState(() => {
    const storedState = localStorage.getItem('selectedState');
    if (!storedState || storedState === 'null') return null;
    try {
      const parsed = JSON.parse(storedState);
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      return [storedState];
    }
  });
  const [searchText, setSearchText] = React.useState('');
  const [birthYear, setBirthYear] = React.useState(null);
  const [coverageAmount, setCoverageAmount] = React.useState(null);
  const [insuranceType, setInsuranceType] = React.useState(null);
  const [isStateModalVisible, setIsStateModalVisible] = React.useState(false);
  const [stateSearchText, setStateSearchText] = React.useState('');
  const [pagination, setPagination] = React.useState({
    current: 1,
    pageSize: 15,
  });
  const [expandedRowKeys, setExpandedRowKeys] = React.useState([]);
  const [isPurchaseModalVisible, setIsPurchaseModalVisible] = React.useState(false);
  const [selectedLead, setSelectedLead] = React.useState(null);
  const [processingLeadId, setProcessingLeadId] = React.useState(null);
  const [dateRange, setDateRange] = React.useState(null);
  const [isCartVisible, setIsCartVisible] = React.useState(false);
  const { cart, addToCart } = useCart();
  const [tableSize, setTableSize] = React.useState(() => {
    return localStorage.getItem('leads-table-size') || 'small';
  });
  const [tourOpen, setTourOpen] = React.useState(false);
  const ref1 = React.useRef(null);
  const ref2 = React.useRef(null);
  const ref3 = React.useRef(null);
  const ref4 = React.useRef(null);
  const ref5 = React.useRef(null);
  const ref6 = React.useRef(null);
  const [searchWizardVisible, setSearchWizardVisible] = React.useState(false);
  const [currentStep, setCurrentStep] = React.useState(0);
  const [tempFilters, setTempFilters] = React.useState({
    states: [],
    dateRange: dateRange,
    birthYears: [],
    coverageAmounts: [],
    insuranceTypes: []
  });
  const [searchMode, setSearchMode] = React.useState('quick'); // 'quick' or 'wizard'
  const [isTourActive, setIsTourActive] = React.useState(false);
  const [isPurchasersModalVisible, setIsPurchasersModalVisible] = React.useState(false);
  const [selectedLeadPurchasers, setSelectedLeadPurchasers] = React.useState([]);
  const [uploadErrorModalVisible, setUploadErrorModalVisible] = useState(false);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [uploadSummary, setUploadSummary] = useState(null);
  const [csvPreviewData, setCsvPreviewData] = useState([]);
  const [csvValidationErrors, setCsvValidationErrors] = useState([]);
  const [csvPreviewVisible, setCsvPreviewVisible] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [csvSearchText, setCsvSearchText] = useState('');
  const [duplicateRecords, setDuplicateRecords] = useState([]);

  // Add this before the columns definition
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);

  // CSV Preview columns - dynamically generated based on CSV data
  const csvPreviewColumns = React.useMemo(() => {
    if (!csvPreviewData || csvPreviewData.length === 0) return [];
    
    const baseColumns = [
      {
        title: 'Row',
        key: 'row',
        width: 60,
        fixed: 'left',
        render: (_, __, index) => index + 2 // +2 because row 1 is header, and index is 0-based
      }
    ];

    // Get all unique keys from the CSV data
    const allKeys = new Set();
    csvPreviewData.forEach(row => {
      Object.keys(row).forEach(key => allKeys.add(key));
    });

    // Create columns for each key (limit to first 10 columns for readability)
    const dataColumns = Array.from(allKeys).slice(0, 10).map(key => ({
      title: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      dataIndex: key,
      key: key,
      width: 150,
      render: (text) => {
        if (text === null || text === undefined || text === '') return '-';
        const str = String(text);
        return str.length > 30 ? `${str.substring(0, 30)}...` : str;
      }
    }));

    // Add validation column at the end
    const validationColumn = {
      title: 'Validation',
      key: 'validation',
      width: 100,
      fixed: 'right',
      render: (_, __, index) => {
        const hasError = csvValidationErrors.some(error => error.row === index + 2);
        return hasError ? (
          <Tag color="red">Invalid</Tag>
        ) : (
          <Tag color="green">Valid</Tag>
        );
      }
    };

    return [...baseColumns, ...dataColumns, validationColumn];
  }, [csvPreviewData, csvValidationErrors]);

  // CSV Validation Error columns
  const csvErrorColumns = [
    {
      title: 'Row',
      dataIndex: 'row',
      key: 'row',
      width: 60
    },
    {
      title: 'Errors',
      dataIndex: 'errors',
      key: 'errors',
      render: (errors) => (
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          {errors.map((error, index) => (
            <li key={index} style={{ color: '#ff4d4f' }}>
              {error}
            </li>
          ))}
        </ul>
      )
    }
  ];

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Coverage amount ranges
  const coverageRanges = [
    { label: '$50,000 - $100,000', value: '50000-100000' },
    { label: '$100,000 - $150,000', value: '100000-150000' },
    { label: '$150,000 - $200,000', value: '150000-200000' },
    { label: '$200,000 - $250,000', value: '200000-250000' },
    { label: '$250,000 - $300,000', value: '250000-300000' },
    { label: '$300,000 - $350,000', value: '300000-350000' },
    { label: '$350,000 - $400,000', value: '350000-400000' },
    { label: '$400,000 - $450,000', value: '400000-450000' },
    { label: '$450,000 - $500,000', value: '450000-500000' },
    { label: '$500,000 - $550,000', value: '500000-550000' },
    { label: '$550,000 - $600,000', value: '550000-600000' },
    { label: '$600,000 - $650,000', value: '600000-650000' },
    { label: '$650,000 - $700,000', value: '650000-700000' },
    { label: '$700,000 - $750,000', value: '700000-750000' },
    { label: '$750,000 - $800,000', value: '750000-800000' },
    { label: '$800,000 - $850,000', value: '800000-850000' },
    { label: '$850,000 - $900,000', value: '850000-900000' },
    { label: '$900,000 - $950,000', value: '900000-950000' },
    { label: '$950,000 - $1,000,000', value: '950000-1000000' },
    { label: '$1,000,000+', value: '1000000-plus' }
  ];

  const { data: leads, isLoading, error, refetch } = useQuery({
    queryKey: ['leads', selectedState, pagination.current, dateRange, birthYear, coverageAmount, insuranceType],
    queryFn: () => {
      const params = {
        page: pagination.current,
        state: Array.isArray(selectedState) ? selectedState.join(',') : selectedState,
        birthYear: Array.isArray(birthYear) ? birthYear.join(',') : birthYear,
        coverageAmount: Array.isArray(coverageAmount) ? coverageAmount.join(',') : coverageAmount,
        insuranceType: Array.isArray(insuranceType) ? insuranceType.join(',') : insuranceType,
      };
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.startDate = dateRange[0].format('YYYY-MM-DD');
        params.endDate = dateRange[1].format('YYYY-MM-DD');
      }
      return leadsService.getLeads(params);
    }
  });

  const { data: states, isLoading: statesLoading } = useQuery({
    queryKey: ['states'],
    queryFn: () => stateService.get()
  });

  // Filter states based on search text
  const filteredStates = React.useMemo(() => {
    if (!states) return [];
    return states.filter(state => 
      state.name.toLowerCase().includes(stateSearchText.toLowerCase())
    );
  }, [states, stateSearchText]);

  const handleStateModalOpen = () => {
    setIsStateModalVisible(true);
    setStateSearchText('');
  };

  const handleStateModalClose = () => {
    setIsStateModalVisible(false);
    setStateSearchText('');
  };

  const handleStateSelect = (state) => {
    setSelectedState(state);
    localStorage.setItem('selectedState', state);
    handleStateModalClose();
  };

  const handleClearState = () => {
    setSelectedState(null);
    localStorage.setItem('selectedState', 'null');
  };

  const deleteMutation = useMutation({
    mutationFn: (id) => leadsService.deleteLead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads', selectedState] });
      message.success('Lead deleted successfully');
    },
    onError: (error) => {
      message.error('Failed to delete lead: ' + error.message);
    }
  });

  const purchaseMutation = useMutation({
    mutationFn: (data) => leadPurchasesService.purchaseLead(data),
    onSuccess: () => {
      message.success('Lead purchased successfully and can be viewed in the Purchases tab');
      queryClient.invalidateQueries({ queryKey: ['leads', selectedState] });
      setProcessingLeadId(null);
    },
    onError: (error) => {
      setProcessingLeadId(null);
      message.error('Failed to purchase lead: ' + error.message);
    }
  });

  const handleView = (record) => {
    setExpandedRowKeys(expandedRowKeys.includes(record.id) 
      ? expandedRowKeys.filter(key => key !== record.id)
      : [...expandedRowKeys, record.id]
    );
  };

  const handleDelete = (id) => {
    let modal = null;
    modal = Modal.confirm({
      title: 'Are you sure you want to delete this lead?',
      content: 'This action cannot be undone.',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        modal.update({ okButtonProps: { loading: true } }); // Show loading indicator
  
        try {
          await deleteMutation.mutateAsync(id);
        } catch (error) {
          return Promise.reject();
        } finally {
          modal.update({ okButtonProps: { loading: false } }); // Reset loading state
        }
      }
    });
  };
  

  const getDetailsData = (record) => {
    if (!record) return [];
    
    const basicInfo = [
      { field: 'Identifier', value: record.identifier },
      { field: 'First Name', value: record.first_name },
      { field: 'Last Name', value: record.last_name },
      { field: 'Phone', value: record.phone },
      { field: 'Email', value: record.email },
      { field: 'Lead Old', value: record.lead_old },
      { field: 'Last Activity', value: record.last_activity },
      { field: 'State', value: record.state_name },
      { field: 'Birth Date', value: record.birth_date }
    ];

    const insuranceInfo = [
      { field: 'Type of Life Insurance', value: record.type_of_life_insurance },
      { field: 'Coverage Amount', value: record.coverage_amount },
      { field: 'Final Expense Coverage', value: record.final_expense_coverage },
      { field: 'Beneficiary', value: record.beneficiary },
      { field: 'Are you looking for additional retirement income sources?', value: record.additional_retirement_income },
      { field: 'Annual Income', value: record.annual_income },
      { field: 'Do you have interest in an annuity?', value: record.guaranteed_account_interest }
    ];

    const contactInfo = [
      // { field: 'Best time of day to contact you', value: record.can_we_contact },
      { field: 'If you are considering mortgage protection, what is the amount owed on your home?', value: record.mortgage_protection_amount },
      // { field: 'Funeral Type', value: record.funeral_type },
      // { field: 'Similar Plan Contact Permission', value: record.similar_plan_contact_permission },
      // { field: 'Mortgage Amount Owed', value: record.mortgage_amount_owed }
    ];

    return [
      { field: 'Basic Information', value: '', isHeader: true },
      ...basicInfo,
      { field: 'Insurance Information', value: '', isHeader: true },
      ...insuranceInfo,
      { field: 'Contact & Mortgage Information', value: '', isHeader: true },
      ...contactInfo
    ];
  };

  const expandedRowRender = (record) => {
    const isHidden = !isAdmin && record.availability_status;
    
    return (
      <div style={{ padding: '16px' }}>
        {!isAdmin && record.availability_status && (
          <div style={{ 
            marginBottom: 16, 
            padding: 16, 
            background: '#fff2f0', 
            border: '1px solid #ffccc7',
            borderRadius: 4
          }}>
            <Typography.Text type="danger">
              <LockOutlined /> This lead's details are locked. Purchase to view complete information.
            </Typography.Text>
          </div>
        )}
        <Table
          columns={[
            {
              title: 'Field',
              dataIndex: 'field',
              key: 'field',
              width: '30%',
            },
            {
              title: 'Value',
              dataIndex: 'value',
              key: 'value',
              width: '70%',
              render: (value, record) => {
                if (record.isHeader) {
                  return <Typography.Text strong>{value}</Typography.Text>;
                }
                // Only hide sensitive fields for non-admin users
                const sensitiveFields = [
                  'Email',
                  'Phone',
                  'Last Activity',
                  'Final Expense Coverage',
                  'Beneficiary',
                  'Are you looking for additional retirement income sources?',
                  'Annual Income',
                  'Do you have interest in an annuity?',
                  'Best time of day to contact you',
                  'If you are considering mortgage protection, what is the amount owed on your home?',
                  'Funeral Type',
                  'Similar Plan Contact Permission',
                  'Mortgage Amount Owed'
                ];
                const shouldHide = !isAdmin && record.availability_status && sensitiveFields.includes(record.field);
                
                return (
                  <div className={shouldHide ? 'table-cell-hidden' : ''}>
                    {value}
                    {shouldHide && (
                      <div className="table-cell-hidden-overlay">
                        <Typography.Text type="secondary">Purchase to view</Typography.Text>
                      </div>
                    )}
                  </div>
                );
              }
            }
          ]}
          dataSource={getDetailsData(record)}
          pagination={false}
          rowKey={(record) => record.field}
          onRow={(record) => ({
            style: record.isHeader ? { 
              background: '#f5f5f5',
              fontWeight: 'bold'
            } : {}
          })}
        />
      </div>
    );
  };

  const handleTableChange = (newPagination, filters, sorter) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize
    });
  };



  const handleConfirmPurchase = async (leadId, paymentMethodId) => {
    setIsPurchaseModalVisible(false);
    try {
      await purchaseMutation.mutateAsync({
        leadId: leadId,
        paymentMethodId: paymentMethodId
      });
    } catch (error) {
      setProcessingLeadId(null);
      message.error('Failed to purchase lead: ' + error.message);
    }
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
  };

  const isLeadInCart = (leadId) => {
    return cart.some(item => item.id === leadId);
  };

  const handleAddToCart = (record) => {
    if (isLeadInCart(record.id)) {
      message.info('This lead is already in your cart');
      return;
    }
    addToCart(record);
    message.success('Lead added to cart');
  };

  // Add this before the columns definition
  const handleViewPurchasers = (record) => {
    setSelectedLeadPurchasers(record.purchasers || []);
    setIsPurchasersModalVisible(true);
    setSelectedLead(record); // Add this line to store the selected lead
  };

  // Define columns first
  const columns = [
    {
      title: 'First Name',
      dataIndex: 'first_name',
      key: 'first_name',
      width: 200,
      render: (text, record) => (
        <span style={{ display: 'flex', alignItems: 'center' }}>
          {!record.availability_status && (
            <span style={{
              display: 'inline-block',
              width: 3,
              height: 16,
              background: '#ff7875',
              borderRadius: 2,
              marginRight: 8
            }} />
          )}
          {text}
        </span>
      ),
    },
    {
      title: 'Last Name',
      dataIndex: 'last_name',
      key: 'last_name',
      width: 200,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 200,
      render: (value, record) => {
        const isHidden = !isAdmin && record.availability_status;
        return (
          <div className={isHidden ? 'table-cell-hidden' : ''}>
            {value}
            {isHidden && (
              <div className="table-cell-hidden-overlay">
                <Typography.Text type="secondary">Purchase to view</Typography.Text>
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: 'Lead Age',
      dataIndex: 'lead_old',
      key: 'lead_old',
      width: 200,
      render: (date) => {
        if (!date) return '';
        return new Date(date).toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric'
        });
      }
    },
    {
      title: 'Last Activity',
      dataIndex: 'last_activity',
      key: 'last_activity',
      width: 200,
      render: (value, record) => {
        const isHidden = !isAdmin && record.availability_status;
        return (
          <div className={isHidden ? 'table-cell-hidden' : ''}>
            {value}
            {isHidden && (
              <div className="table-cell-hidden-overlay">
                <Typography.Text type="secondary">Purchase to view</Typography.Text>
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: 'State',
      dataIndex: 'state_name',
      key: 'state_name',
      width: 200,
    },
    {
      title: 'Birth Date',
      dataIndex: 'birth_date',
      key: 'birth_date',
      width: 200,
      render: (value) => {
        if (!value) return '';
        return new Date(value).toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric'
        });
      }
    },
    {
      title: 'Coverage Amount',
      dataIndex: 'coverage_amount',
      key: 'coverage_amount',
      width: 200,
      render: (value) => value || '-'
    },
    {
      title: 'Insurance Type',
      dataIndex: 'type_of_life_insurance',
      key: 'type_of_life_insurance',
      width: 200,
      render: (value) => value || '-'
    },
    {
      title: 'Final Expense Coverage',
      dataIndex: 'final_expense_coverage',
      key: 'final_expense_coverage',
      width: 200,
      render: (value, record) => {
        const isHidden = !isAdmin && record.availability_status;
        return (
          <div className={isHidden ? 'table-cell-hidden' : ''}>
            {value}
            {isHidden && (
              <div className="table-cell-hidden-overlay">
                <Typography.Text type="secondary">Purchase to view</Typography.Text>
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: 'Beneficiary',
      dataIndex: 'beneficiary',
      key: 'beneficiary',
      width: 200,
      render: (value, record) => {
        const isHidden = !isAdmin && record.availability_status;
        return (
          <div className={isHidden ? 'table-cell-hidden' : ''}>
            {value}
            {isHidden && (
              <div className="table-cell-hidden-overlay">
                <Typography.Text type="secondary">Purchase to view</Typography.Text>
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: 'Are you looking for additional retirement income sources?',
      dataIndex: 'additional_retirement_income',
      key: 'additional_retirement_income',
      width: 250,
      render: (value, record) => {
        const isHidden = !isAdmin && record.availability_status;
        return (
          <div className={isHidden ? 'table-cell-hidden' : ''}>
            {value}
            {isHidden && (
              <div className="table-cell-hidden-overlay">
                <Typography.Text type="secondary">Purchase to view</Typography.Text>
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: 'Annual Income',
      dataIndex: 'annual_income',
      key: 'annual_income',
      width: 200,
      render: (value, record) => {
        const isHidden = !isAdmin && record.availability_status;
        return (
          <div className={isHidden ? 'table-cell-hidden' : ''}>
            {value}
            {isHidden && (
              <div className="table-cell-hidden-overlay">
                <Typography.Text type="secondary">Purchase to view</Typography.Text>
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: 'Do you have interest in an annuity?',
      dataIndex: 'guaranteed_account_interest',
      key: 'guaranteed_account_interest',
      width: 220,
      render: (value, record) => {
        const isHidden = !isAdmin && record.availability_status;
        return (
          <div className={isHidden ? 'table-cell-hidden' : ''}>
            {value}
            {isHidden && (
              <div className="table-cell-hidden-overlay">
                <Typography.Text type="secondary">Purchase to view</Typography.Text>
              </div>
            )}
          </div>
        );
      }
    },
    // {
    //   title: 'Best time of day to contact you',
    //   dataIndex: 'can_we_contact',
    //   key: 'can_we_contact',
    //   width: 200,
    //   render: (value, record) => {
    //     const isHidden = !isAdmin && record.availability_status;
    //     return (
    //       <div className={isHidden ? 'table-cell-hidden' : ''}>
    //         {value}
    //         {isHidden && (
    //           <div className="table-cell-hidden-overlay">
    //             <Typography.Text type="secondary">Purchase to view</Typography.Text>
    //           </div>
    //         )}
    //       </div>
    //     );
    //   }
    // },
    {
      title: 'If you are considering mortgage protection, what is the amount owed on your home?',
      dataIndex: 'mortgage_protection_amount',
      key: 'mortgage_protection_amount',
      width: 230,
      render: (value, record) => {
        const isHidden = !isAdmin && record.availability_status;
        return (
          <div className={isHidden ? 'table-cell-hidden' : ''}>
            {value}
            {isHidden && (
              <div className="table-cell-hidden-overlay">
                <Typography.Text type="secondary">Purchase to view</Typography.Text>
              </div>
            )}
          </div>
        );
      }
    },
    // {
    //   title: 'Funeral Type',
    //   dataIndex: 'funeral_type',
    //   key: 'funeral_type',
    //   width: 200,
    //   render: (value, record) => {
    //     const isHidden = !isAdmin && record.availability_status;
    //     return (
    //       <div className={isHidden ? 'table-cell-hidden' : ''}>
    //         {value}
    //         {isHidden && (
    //           <div className="table-cell-hidden-overlay">
    //             <Typography.Text type="secondary">Purchase to view</Typography.Text>
    //           </div>
    //         )}
    //       </div>
    //     );
    //   }
    // },
    // {
    //   title: 'Similar Plan Contact Permission',
    //   dataIndex: 'similar_plan_contact_permission',
    //   key: 'similar_plan_contact_permission',
    //   width: 240,
    //   render: (value, record) => {
    //     const isHidden = !isAdmin && record.availability_status;
    //     return (
    //       <div className={isHidden ? 'table-cell-hidden' : ''}>
    //         {value}
    //         {isHidden && (
    //           <div className="table-cell-hidden-overlay">
    //             <Typography.Text type="secondary">Purchase to view</Typography.Text>
    //           </div>
    //         )}
    //       </div>
    //     );
    //   }
    // },
    // {
    //   title: 'Mortgage Amount Owed',
    //   dataIndex: 'mortgage_amount_owed',
    //   key: 'mortgage_amount_owed',
    //   width: 200,
    //   // render: (value, record) => {
    //   //   const isHidden = !isAdmin && record.availability_status;
    //   //   return (
    //   //     <div className={isHidden ? 'table-cell-hidden' : ''}>
    //   //       {value}
    //   //       {isHidden && (
    //   //         <div className="table-cell-hidden-overlay">
    //   //           <Typography.Text type="secondary">Purchase to view</Typography.Text>
    //   //         </div>
    //   //       )}
    //   //     </div>
    //   //   );
    //   // }
    // },
    {
      title: 'Price',
      dataIndex: 'lead_price',
      key: 'lead_price',
      width: 80,
      fixed: isMobile ? false : 'right',
      render: (price) => <Badge count={`$${parseFloat(price || 0).toFixed(2)}`} color="black" />
    },
    ...(isAdmin ? [
      {
        title: 'Purchases',
        key: 'total_purchases',
        width: 120,
        fixed: isMobile ? false : 'right',
        render: (_, record) => {
          const purchaseCount = record.purchasers?.length || 0;
          return (
              <>
              {purchaseCount > 0 ? (
                <>
                <Badge count={`${purchaseCount} Purchases`} color="blue" />
                </>
              ) : (
                <Badge count={0} color="default" />
              )}
              </>
          );
        }
      },
      {
        title: 'Returns',
        dataIndex: 'return_count',
        key: 'return_count',
        width: 120,
        fixed: isMobile ? false : 'right',
        render: (count) => (
            <>
            {count > 0 ? (
                <Badge count={`${count} Returns`} color="red" />
            ) : (
              null
            )}
            </>
        ),
      },
      {
        title: 'Revenue',
        key: 'revenue',
        width: 120,
        fixed: isMobile ? false : 'right',
        render: (_, record) => {
          const totalRevenue = record.purchase_count * record.lead_price;
          const returnDeduction = record.return_count * record.lead_price;
          const netRevenue = totalRevenue - returnDeduction;
          return (
            <Space direction="vertical" size={0}>
              <Typography.Text strong style={{ color: netRevenue >= 0 ? '#52c41a' : '#ff4d4f' }}>
                ${netRevenue.toFixed(2)}
              </Typography.Text>
              {record.return_count > 0 && (
                <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                  -${returnDeduction.toFixed(2)} from returns
                </Typography.Text>
              )}
            </Space>
          );
        },
      },
    ] : []),
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      fixed: isMobile ? false : 'right',
      render: (_, record) => (
        <Space ref={ref5}>
          {isAdmin && (
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'history',
                    icon: <EyeOutlined />,
                    label: 'View History',
                    onClick: () => handleViewPurchasers(record),
                    disabled: !record.purchasers?.length
                  },
                  {
                    key: 'delete',
                    icon: <DeleteOutlined />,
                    label: 'Delete Lead',
                    danger: true,
                    onClick: () => handleDelete(record.id)
                  }
                ]
              }}
              trigger={['click']}
            >
              <Button type="text" icon={<SettingOutlined />} />
            </Dropdown>
          )}
          {!isAdmin && record.availability_status && (
            <Button 
              type="link"
              icon={<ShoppingCartOutlined />}
              onClick={() => handleAddToCart(record)}
              disabled={isLeadInCart(record.id)}
              style={{
                color: isLeadInCart(record.id) ? '#52c41a' : undefined
              }}
            />
          )}
        </Space>
      ),
    },
   
    
  ];
  const {
    visibleKeys: visibleColumnKeys,
    setColumnVisibility,
    resetColumns,
    isVisible
  } = useColumnVisibility('leads-table', columns);
  const filteredColumns = columns.filter(col => isVisible(col.key || col.dataIndex));

  const [customizeModalOpen, setCustomizeModalOpen] = React.useState(false);
  const [columnSearch, setColumnSearch] = React.useState('');

  const filteredColumnOptions = columns.filter(col => {
    if (!columnSearch) return true;
    return (col.title || '').toLowerCase().includes(columnSearch.toLowerCase());
  });

  const handleTableSizeChange = (val) => {
    setTableSize(val);
    localStorage.setItem('leads-table-size', val);
  };

  // Add this function to handle tour state
  const handleTourStart = () => {
    setIsTourActive(true);
    setTourOpen(true);
  };

  const handleTourEnd = () => {
    setIsTourActive(false);
    setTourOpen(false);
  };

  // Modify the tour steps to include disabled state
  const tourSteps = [
    {
      title: 'Welcome to Lead Management',
      description: 'This is your central hub for managing and finding leads. Let\'s explore the key features.',
      target: () => ref1.current,
      disabled: true
    },
    {
      title: 'Quick Stats',
      description: 'View important statistics about your leads at a glance.',
      target: () => ref2.current,
      disabled: true
    },
    {
      title: 'Advanced Search',
      description: 'Access powerful search capabilities to find the perfect leads for your business.',
      target: () => ref3.current,
      disabled: true
    },
    {
      title: 'Search Wizard',
      description: 'Switch to the Search Wizard for a guided, step-by-step process to find leads. Perfect for complex searches.',
      target: () => document.querySelector('.ant-segmented'),
      disabled: true
    },
    {
      title: 'Results Table',
      description: 'View and manage your leads in this customizable table. You can adjust the size and visible columns.',
      target: () => ref4.current,
      disabled: true
    },
    {
      title: 'Customize View',
      description: 'Click the settings icon to customize which columns you want to see in the table. You can show/hide columns and reset to default view.',
      target: () => ref6.current,
      disabled: true
    },
    {
      title: 'Cart & Actions',
      description: 'Add leads to your cart for bulk purchase and perform actions like viewing details or purchasing individual leads.',
      target: () => ref5.current,
      disabled: true
    }
  ];

  const wizardSteps = [
    {
      title: 'Location',
      icon: <EnvironmentOutlined />
    },
    {
      title: 'Time Range',
      icon: <ClockCircleOutlined />
    },
    {
      title: 'Birth Year',
      icon: <CalendarOutlined />
    },
    {
      title: 'Coverage',
      icon: <DollarOutlined />
    },
    {
      title: 'Insurance',
      icon: <InsuranceOutlined />
    }
  ];

  const handleWizardNext = () => {
    setCurrentStep(currentStep + 1);
  };

  const handleWizardPrev = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleWizardFinish = () => {
    // Handle multiple selections
    setSelectedState(tempFilters.states.length > 0 ? tempFilters.states : null);
    setDateRange(tempFilters.dateRange);
    setBirthYear(tempFilters.birthYears.length > 0 ? tempFilters.birthYears : null);
    setCoverageAmount(tempFilters.coverageAmounts.length > 0 ? tempFilters.coverageAmounts : null);
    setInsuranceType(tempFilters.insuranceTypes.length > 0 ? tempFilters.insuranceTypes : null);
    setSearchWizardVisible(false);
    setCurrentStep(0);
    setTempFilters({
      states: [],
      dateRange: null,
      birthYears: [],
      coverageAmounts: [],
      insuranceTypes: []
    });
  };

  const handleWizardCancel = () => {
    setSearchWizardVisible(false);
    setCurrentStep(0);
    setTempFilters({
      states: [],
      dateRange: dateRange,
      birthYears: [],
      coverageAmounts: [],
      insuranceTypes: []
    });
  };

  const renderWizardStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div style={{ padding: '24px' }}>
            <Input
              size="large"
              placeholder="Search states..."
              value={stateSearchText}
              onChange={(e) => setStateSearchText(e.target.value)}
              prefix={<SearchOutlined />}
              style={{ marginBottom: '16px' }}
            />
            <div style={{ 
              maxHeight: '400px', 
              overflowY: 'auto',
              padding: '16px',
              backgroundColor: '#fafafa',
              borderRadius: '8px'
            }}>
              <Row gutter={[8, 8]}>
                {filteredStates.map(state => (
                  <Col xs={12} sm={8} md={6} key={state.id}>
                    <div
                      style={{
                        padding: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.3s',
                        borderRadius: '4px',
                        border: '1px solid #f0f0f0',
                        backgroundColor: tempFilters.states.includes(state.name) ? '#e6f7ff' : 'white',
                        boxShadow: tempFilters.states.includes(state.name) ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
                      }}
                      onClick={() => {
                        const newStates = tempFilters.states.includes(state.name)
                          ? tempFilters.states.filter(s => s !== state.name)
                          : [...tempFilters.states, state.name];
                        setTempFilters({ ...tempFilters, states: newStates });
                      }}
                    >
                      <Tag
                        color={tempFilters.states.includes(state.name) ? 'blue' : 'default'}
                        style={{
                          width: '100%',
                          textAlign: 'center',
                          padding: '8px',
                          margin: 0,
                          fontSize: '14px',
                          borderRadius: '4px',
                          border: 'none',
                          backgroundColor: tempFilters.states.includes(state.name) ? '#e6f7ff' : '#f5f5f5',
                          color: tempFilters.states.includes(state.name) ? '#1890ff' : 'rgba(0, 0, 0, 0.85)'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>{state.name}</span>
                          <Badge 
                            count={state.available_leads_count} 
                            style={{ 
                              backgroundColor: '#52c41a',
                              marginLeft: '8px'
                            }} 
                          />
                        </div>
                      </Tag>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          </div>
        );
      case 1:
        return (
          <div style={{ padding: '24px' }}>
            <RangePicker
              size="large"
              style={{ width: '100%' }}
              onChange={(dates) => setTempFilters({ ...tempFilters, dateRange: dates })}
              value={tempFilters.dateRange}
              placeholder={['Start Date', 'End Date']}
              allowClear
            />
          </div>
        );
      case 2:
        return (
          <div style={{ padding: '24px' }}>
            <Select
              mode="multiple"
              size="large"
              style={{ width: '100%' }}
              placeholder="Select birth years"
              value={tempFilters.birthYears}
              onChange={(values) => setTempFilters({ ...tempFilters, birthYears: values })}
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toString().indexOf(input) >= 0
              }
            >
              {Array.from({ length: 100 }, (_, i) => {
                const year = new Date().getFullYear() - i;
                return (
                  <Option key={year} value={year}>
                    {year}
                  </Option>
                );
              })}
            </Select>
          </div>
        );
      case 3:
        return (
          <div style={{ padding: '24px' }}>
            <Select
              mode="multiple"
              size="large"
              style={{ width: '100%' }}
              placeholder="Select coverage amounts"
              value={tempFilters.coverageAmounts}
              onChange={(values) => setTempFilters({ ...tempFilters, coverageAmounts: values })}
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {coverageRanges.map(range => (
                <Option key={range.value} value={range.value}>
                  {range.label}
                </Option>
              ))}
            </Select>
          </div>
        );
      case 4:
        return (
          <div style={{ padding: '24px' }}>
            <Select
              mode="multiple"
              size="large"
              style={{ width: '100%' }}
              placeholder="Select insurance types"
              value={tempFilters.insuranceTypes}
              onChange={(values) => setTempFilters({ ...tempFilters, insuranceTypes: values })}
              allowClear
            >
              <Option value="Permanent Life Insurance">Permanent Life Insurance</Option>
              <Option value="Final Expense">Final Expense</Option>
              <Option value="Mortgage Protection">Mortgage Protection</Option>
            </Select>
          </div>
        );
      default:
        return null;
    }
  };

  React.useEffect(() => {
    // Check if it's the user's first visit
    const hasSeenTour = localStorage.getItem('hasSeenLeadsTour');
    if (!hasSeenTour) {
      setTourOpen(true);
      localStorage.setItem('hasSeenLeadsTour', 'true');
    }
  }, []);

  const handleSearchModeChange = (value) => {
    setSearchMode(value);
    setSelectedState(null);
    setDateRange(null);
    setBirthYear(null);
    setCoverageAmount(null);
    setInsuranceType(null);
    localStorage.setItem('selectedState', 'null');
  };

  const handleRemoveFilter = (type, value) => {
    switch (type) {
      case 'state':
        setSelectedState(Array.isArray(selectedState) 
          ? selectedState.filter(s => s !== value)
          : null);
        break;
      case 'date':
        setDateRange(null);
        break;
      case 'year':
        setBirthYear(Array.isArray(birthYear)
          ? birthYear.filter(y => y !== value)
          : null);
        break;
      case 'coverage':
        setCoverageAmount(Array.isArray(coverageAmount)
          ? coverageAmount.filter(c => c !== value)
          : null);
        break;
      case 'insurance':
        setInsuranceType(Array.isArray(insuranceType)
          ? insuranceType.filter(i => i !== value)
          : null);
        break;
    }
  };

  const handleRemoveTempFilter = (type, value) => {
    switch (type) {
      case 'state':
        setTempFilters(prev => ({
          ...prev,
          states: prev.states.filter(s => s !== value)
        }));
        break;
      case 'date':
        setTempFilters(prev => ({
          ...prev,
          dateRange: null
        }));
        break;
      case 'year':
        setTempFilters(prev => ({
          ...prev,
          birthYears: prev.birthYears.filter(y => y !== value)
        }));
        break;
      case 'coverage':
        setTempFilters(prev => ({
          ...prev,
          coverageAmounts: prev.coverageAmounts.filter(c => c !== value)
        }));
        break;
      case 'insurance':
        setTempFilters(prev => ({
          ...prev,
          insuranceTypes: prev.insuranceTypes.filter(i => i !== value)
        }));
        break;
    }
  };

  const handleClearAllFilters = () => {
    setSelectedState(null);
    setDateRange(null);
    setBirthYear(null);
    setCoverageAmount(null);
    setInsuranceType(null);
  };

  const handleClearAllTempFilters = () => {
    setTempFilters({
      states: [],
      dateRange: null,
      birthYears: [],
      coverageAmounts: [],
      insuranceTypes: []
    });
  };

  // CSV Parsing function
  const parseCSV = (csvText) => {
    const lines = csvText.split('\n').filter(line => line.trim());
    
    if (lines.length < 2) {
      message.error('CSV file must have header and at least one data row');
      return null;
    }

    // Parse CSV - handle quoted values
    const parseCSVLine = (line) => {
      const result = [];
      let current = '';
      let inQuotes = false;
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      result.push(current.trim());
      return result;
    };

    const headers = parseCSVLine(lines[0]).map(h => h.replace(/"/g, '').trim());
    const data = [];

    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]).map(v => v.replace(/^"|"$/g, '').trim());
      if (values.length === headers.length) {
        const row = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        data.push(row);
      }
    }

    return data.length > 0 ? data : null;
  };

  // Validation function
  const validateCSVData = (data) => {
    const errors = [];
    const identifiers = new Map(); // Map to track which rows have which identifiers
    const emails = new Map();
    const phones = new Map();
    const duplicates = [];
    
    // First pass: collect all identifiers, emails, and phones
    data.forEach((row, index) => {
      const rowNumber = index + 2;
      
      if (row.identifier && row.identifier.trim() !== '') {
        const identifier = row.identifier.trim().toLowerCase();
        if (!identifiers.has(identifier)) {
          identifiers.set(identifier, []);
        }
        identifiers.get(identifier).push({ row: rowNumber, data: row });
      }

      if (row.email && row.email.trim() !== '') {
        const email = row.email.trim().toLowerCase();
        if (!emails.has(email)) {
          emails.set(email, []);
        }
        emails.get(email).push({ row: rowNumber, data: row });
      }

      if (row.phone && row.phone.trim() !== '') {
        const phone = row.phone.trim().replace(/[^\d]/g, ''); // Normalize phone number
        if (phone) {
          if (!phones.has(phone)) {
            phones.set(phone, []);
          }
          phones.get(phone).push({ row: rowNumber, data: row });
        }
      }
    });

    // Second pass: validate each row
    data.forEach((row, index) => {
      const rowErrors = [];
      const rowNumber = index + 2; // +2 because row 1 is header, and index is 0-based

      // Check for duplicate identifiers
      if (row.identifier && row.identifier.trim() !== '') {
        const identifier = row.identifier.trim().toLowerCase();
        const identifierRows = identifiers.get(identifier) || [];
        if (identifierRows.length > 1) {
          const duplicateRows = identifierRows.filter(r => r.row !== rowNumber).map(r => r.row);
          rowErrors.push(`Duplicate identifier found in rows: ${duplicateRows.join(', ')}`);
        }
      }

      // Check for duplicate emails
      if (row.email && row.email.trim() !== '') {
        const email = row.email.trim().toLowerCase();
        const emailRows = emails.get(email) || [];
        if (emailRows.length > 1) {
          const duplicateRows = emailRows.filter(r => r.row !== rowNumber).map(r => r.row);
          rowErrors.push(`Duplicate email found in rows: ${duplicateRows.join(', ')}`);
        }
      }

      // Check for duplicate phones
      if (row.phone && row.phone.trim() !== '') {
        const phone = row.phone.trim().replace(/[^\d]/g, '');
        if (phone) {
          const phoneRows = phones.get(phone) || [];
          if (phoneRows.length > 1) {
            const duplicateRows = phoneRows.filter(r => r.row !== rowNumber).map(r => r.row);
            rowErrors.push(`Duplicate phone found in rows: ${duplicateRows.join(', ')}`);
          }
        }
      }

      // Validate birth_date format (MM/DD/YYYY)
      if (row.birth_date && row.birth_date.trim() !== '') {
        const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
        if (!dateRegex.test(row.birth_date.trim())) {
          rowErrors.push('Birth date must be in MM/DD/YYYY format (e.g., 01/15/1990)');
        }
      }

      // Validate lead_old format (YYYY-MM-DD HH:mm:ss)
      if (row.lead_old && row.lead_old.trim() !== '') {
        const dateTimeRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
        if (!dateTimeRegex.test(row.lead_old.trim())) {
          rowErrors.push('Lead old must be in YYYY-MM-DD HH:mm:ss format (e.g., 2024-03-15 14:30:00)');
        }
      }

      // Validate state (should be a valid US state name or abbreviation)
      if (row.state_name && row.state_name.trim() !== '') {
        // Basic validation - can be enhanced with actual state list
        const stateName = row.state_name.trim();
        if (stateName.length < 2) {
          rowErrors.push('State name must be a valid US state name or abbreviation');
        }
      }

      if (rowErrors.length > 0) {
        errors.push({
          row: rowNumber,
          errors: rowErrors,
          data: row
        });
      }
    });

    // Collect duplicate information for display
    const duplicateInfo = [];
    
    // Find duplicate identifiers
    identifiers.forEach((rows, identifier) => {
      if (rows.length > 1) {
        duplicateInfo.push({
          type: 'identifier',
          value: rows[0].data.identifier,
          rows: rows.map(r => r.row),
          count: rows.length
        });
      }
    });

    // Find duplicate emails
    emails.forEach((rows, email) => {
      if (rows.length > 1) {
        duplicateInfo.push({
          type: 'email',
          value: rows[0].data.email,
          rows: rows.map(r => r.row),
          count: rows.length
        });
      }
    });

    // Find duplicate phones
    phones.forEach((rows, phone) => {
      if (rows.length > 1) {
        duplicateInfo.push({
          type: 'phone',
          value: rows[0].data.phone,
          rows: rows.map(r => r.row),
          count: rows.length
        });
      }
    });

    setDuplicateRecords(duplicateInfo);

    return errors;
  };

  // Handle file selection and preview
  const handleFileSelect = (file) => {
    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      message.error('Please upload a CSV file');
      return false;
    }

    if (file.size > 10 * 1024 * 1024) {
      message.error('File size must be less than 10MB');
      return false;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csvText = e.target.result;
        const parsedData = parseCSV(csvText);
        
        if (!parsedData) {
          message.error('No valid data rows found in CSV file');
          return;
        }

        setCsvPreviewData(parsedData);
        const validationErrors = validateCSVData(parsedData);
        setCsvValidationErrors(validationErrors);
        setCsvPreviewVisible(true);
        message.success(`Loaded ${parsedData.length} records from CSV`);
      } catch (error) {
        console.error('Error parsing CSV:', error);
        message.error('Error parsing CSV file');
      }
    };
    reader.readAsText(file);
    
    return false; // Prevent auto upload
  };

  // Handle actual upload
  const handleUpload = async () => {
    if (csvPreviewData.length === 0) {
      message.error('Please upload a CSV file first');
      return;
    }

    if (csvValidationErrors.length > 0) {
      message.error('Please fix validation errors before uploading');
      return;
    }

    if (duplicateRecords.length > 0) {
      message.error('Please fix duplicate records before uploading');
      return;
    }

    try {
      setIsUploading(true);
      
      // Convert CSV data back to file
      const csvContent = [
        Object.keys(csvPreviewData[0]).join(','),
        ...csvPreviewData.map(row => 
          Object.keys(csvPreviewData[0]).map(key => `"${row[key] || ''}"`).join(',')
        )
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const file = new File([blob], 'leads.csv', { type: 'text/csv' });
      
    const formData = new FormData();
    formData.append('csv_file', file);

      const response = await leadsService.uploadLeadsCsv(formData);

      // Store upload summary
      setUploadSummary({
        records_imported: response.records_imported || 0,
        records_skipped: response.records_skipped || 0,
        total: (response.records_imported || 0) + (response.records_skipped || 0)
      });

      // Show success message with details
      if (response.records_skipped > 0) {
        setUploadErrors(response.skipped_records_details || []);
        setUploadErrorModalVisible(true);
        
        // Show warning message
        message.warning({
          content: (
            <div>
              <p><strong>{response.records_imported} records imported successfully</strong></p>
              <p>{response.records_skipped} records were skipped. Click to view details.</p>
            </div>
          ),
          duration: 8,
          onClick: () => setUploadErrorModalVisible(true)
        });
      } else {
        message.success(`${response.records_imported} records imported successfully`);
        setUploadErrors([]);
      }
      
      // Clear preview data
      setCsvPreviewData([]);
      setCsvValidationErrors([]);
      setCsvPreviewVisible(false);
      setCsvSearchText('');
      setDuplicateRecords([]);
      
      refetch();
    } catch (error) {
      console.error('Upload error:', error);
      let errorDetails = [];
      
      // Check if response has skipped records details (partial success)
      if (error.response?.data?.skipped_records_details) {
        errorDetails = error.response.data.skipped_records_details;
        setUploadSummary({
          records_imported: error.response.data.records_imported || 0,
          records_skipped: error.response.data.records_skipped || errorDetails.length,
          total: (error.response.data.records_imported || 0) + (error.response.data.records_skipped || errorDetails.length)
        });
      } else if (error.response?.data?.errors) {
        // Handle structured error response
        errorDetails = error.response.data.errors;
        setUploadSummary({
          records_imported: 0,
          records_skipped: errorDetails.length,
          total: errorDetails.length
        });
      } else if (error.response?.data?.error) {
        // Handle single error message
        errorDetails = [{
          row: 'N/A',
          reason: error.response.data.error,
          data: null
        }];
        setUploadSummary({
          records_imported: 0,
          records_skipped: 1,
          total: 1
        });
      } else if (error.response?.status === 500) {
        // Handle 500 error
        errorDetails = [{
          row: 'N/A',
          reason: 'Server error occurred while processing the file. Please check your file format and data.',
          data: null
        }];
        setUploadSummary({
          records_imported: 0,
          records_skipped: 1,
          total: 1
        });
      } else {
        // Handle other errors
        errorDetails = [{
          row: 'N/A',
          reason: error.message || 'Failed to upload CSV file. Please try again.',
          data: null
        }];
        setUploadSummary({
          records_imported: 0,
          records_skipped: 1,
          total: 1
        });
      }

      setUploadErrors(errorDetails);
      setUploadErrorModalVisible(true);
    } finally {
      setIsUploading(false);
    }
  };

  if (error) {
    return <Typography.Text type="danger">Error loading leads: {error.message}</Typography.Text>;
  }

  return (
    <Elements stripe={stripePromise}>
      <div className="page-container" style={{
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)',
        minHeight: '100vh',
        padding: '24px'
      }}>
        <div className="page-header" style={{ 
          marginBottom: '32px',
          padding: '24px',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '300px',
            height: '300px',
            background: 'radial-gradient(circle at top right, rgba(24,144,255,0.1) 0%, rgba(24,144,255,0) 70%)',
            pointerEvents: 'none'
          }} />
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            gap: '24px',
            position: 'relative',
            zIndex: 1
          }}>
            <div ref={ref1}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px',
                marginBottom: '8px'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(24,144,255,0.25)',
                  transition: 'transform 0.3s ease'
                }}>
                  <SearchOutlined style={{ fontSize: '20px', color: '#fff' }} />
                </div>
                <Typography.Title level={2} style={{ 
                  margin: 0, 
                  color: '#1f1f1f',
                  background: 'linear-gradient(135deg, #1f1f1f 0%, #434343 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontSize: '24px'
                }}>
                  Lead Management System
                </Typography.Title>
              </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '16px',
                marginLeft: '52px',
                flexWrap: 'wrap'
              }}>
                <Typography.Text type="secondary" style={{ 
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '4px 12px',
                  background: '#f5f5f5',
                  borderRadius: '16px',
                  transition: 'all 0.3s ease'
                }}>
                  <ClockCircleOutlined />
                  Manage and filter your leads efficiently
                </Typography.Text>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  padding: '4px 12px',
                  background: '#f0f5ff',
                  borderRadius: '16px',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  ':hover': {
                    background: '#e6f7ff',
                    transform: 'translateY(-1px)'
                  }
                }}>
                  <Badge status="success" />
                  <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                    {leads?.meta.total || 0} Leads Available
                  </Typography.Text>
              </div>
                {/* <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  padding: '4px 12px',
                  background: '#f6ffed',
                  borderRadius: '16px'
                }}>
                <Badge status="processing" />
                  <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                    {leads?.data?.filter(lead => lead.availability_status).length || 0} Active Leads
                </Typography.Text>
                </div> */}
              </div>
            </div>
            <Space>
              <Button
                type="default"
                icon={<QuestionCircleOutlined />}
                onClick={handleTourStart}
                style={{
                  height: '40px',
                  padding: '0 20px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                Start Tour
              </Button>
              {isAdmin && (
                <Upload
                  name="csv_file"
                  accept=".csv"
                  showUploadList={false}
                  beforeUpload={handleFileSelect}
                >
                  <Button 
                    type="primary" 
                    icon={<UploadOutlined />} 
                    size="large"
                    style={{
                      height: '40px',
                      padding: '0 20px',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                      border: 'none',
                      boxShadow: '0 2px 8px rgba(24,144,255,0.25)',
                      transition: 'all 0.3s ease',
                      fontSize: '14px',
                      ':hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(24,144,255,0.35)'
                      }
                    }}
                  >
                    Upload CSV
                  </Button>
                </Upload>
              )}
            </Space>
          </div>
        </div>

        {/* Quick stats section */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '24px'
        }} ref={ref2}>
          {/* State Card */}
          <Card className="stat-card" style={{background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',borderRadius: '12px',boxShadow: '0 4px 12px rgba(0,0,0,0.1)'}}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{width: '36px',height: '36px',background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',borderRadius: '8px',display: 'flex',alignItems: 'center',justifyContent: 'center'}}>
                <EnvironmentOutlined style={{ fontSize: '18px', color: '#fff' }} />
              </div>
              <div>
                <Typography.Text type="secondary" style={{ fontSize: '12px' }}>Selected State</Typography.Text>
                <Typography.Title level={5} style={{ margin: '2px 0 0 0', fontSize: '14px' }}>
                  {Array.isArray(selectedState)
                    ? (selectedState.length > 0 ? selectedState.join(', ') : 'All States')
                    : (selectedState || 'All States')}
                </Typography.Title>
          </div>
        </div>
          </Card>

          {/* Lead Age Card */}
          <Card className="stat-card" style={{background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',borderRadius: '12px',boxShadow: '0 4px 12px rgba(0,0,0,0.1)'}}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{width: '36px',height: '36px',background: 'linear-gradient(135deg, #13c2c2 0%, #08979c 100%)',borderRadius: '8px',display: 'flex',alignItems: 'center',justifyContent: 'center'}}>
                <ClockCircleOutlined style={{ fontSize: '18px', color: '#fff' }} />
              </div>
              <div>
                <Typography.Text type="secondary" style={{ fontSize: '12px' }}>Lead Age</Typography.Text>
                <Typography.Title level={5} style={{ margin: '2px 0 0 0', fontSize: '14px' }}>{dateRange ? `${dateRange[0].format('MMM D')} - ${dateRange[1].format('MMM D')}` : 'All Time'}</Typography.Title>
          </div>
        </div>
          </Card>

          {/* Birth Year Card */}
          <Card className="stat-card" style={{background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',borderRadius: '12px',boxShadow: '0 4px 12px rgba(0,0,0,0.1)'}}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{width: '36px',height: '36px',background: 'linear-gradient(135deg, #faad14 0%, #d48806 100%)',borderRadius: '8px',display: 'flex',alignItems: 'center',justifyContent: 'center'}}>
                <CalendarOutlined style={{ fontSize: '18px', color: '#fff' }} />
              </div>
              <div>
                <Typography.Text type="secondary" style={{ fontSize: '12px' }}>Birth Year</Typography.Text>
                <Typography.Title level={5} style={{ margin: '2px 0 0 0', fontSize: '14px' }}>{birthYear ? `${birthYear}` : 'All Years'}</Typography.Title>
              </div>
            </div>
          </Card>

          {/* Coverage Amount Card */}
          <Card className="stat-card" style={{background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',borderRadius: '12px',boxShadow: '0 4px 12px rgba(0,0,0,0.1)'}}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{width: '36px',height: '36px',background: 'linear-gradient(135deg, #fa8c16 0%, #d46b08 100%)',borderRadius: '8px',display: 'flex',alignItems: 'center',justifyContent: 'center'}}>
                <DollarOutlined style={{ fontSize: '18px', color: '#fff' }} />
              </div>
              <div>
                <Typography.Text type="secondary" style={{ fontSize: '12px' }}>Coverage Amount</Typography.Text>
                <Typography.Title level={5} style={{ margin: '2px 0 0 0', fontSize: '14px' }}>{coverageAmount ? coverageRanges.find(r => r.value === coverageAmount)?.label : 'All Ranges'}</Typography.Title>
              </div>
            </div>
          </Card>

          {/* Insurance Type Card */}
          <Card className="stat-card" style={{background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',borderRadius: '12px',boxShadow: '0 4px 12px rgba(0,0,0,0.1)'}}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{width: '36px',height: '36px',background: 'linear-gradient(135deg, #722ed1 0%, #531dab 100%)',borderRadius: '8px',display: 'flex',alignItems: 'center',justifyContent: 'center'}}>
                <InsuranceOutlined style={{ fontSize: '18px', color: '#fff' }} />
              </div>
              <div>
                <Typography.Text type="secondary" style={{ fontSize: '12px' }}>Insurance Type</Typography.Text>
                <Typography.Title level={5} style={{ margin: '2px 0 0 0', fontSize: '14px' }}>{insuranceType || 'All Types'}</Typography.Title>
              </div>
            </div>
          </Card>
        </div>

        {/* Search Panel with Toggle */}
        <Card 
          className="shadow-sm search-panel" 
          ref={ref3}
          style={{ 
            marginBottom: 24,
            backgroundColor: '#fff',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            pointerEvents: isTourActive ? 'none' : 'auto',
            opacity: isTourActive ? 0.7 : 1
          }}
        >
          <div className="search-panel-header-responsive" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <Typography.Title level={4} style={{ margin: 0, wordBreak: 'break-word', fontSize: '2rem' }}>Advanced Search</Typography.Title>
              <Typography.Text type="secondary" style={{ fontSize: '1rem', wordBreak: 'break-word' }}>Find the perfect leads for your business</Typography.Text>
            </div>
            <Segmented
              options={[
                { label: 'Quick Search', value: 'quick' },
                { label: 'Search Wizard', value: 'wizard' }
              ]}
              value={searchMode}
              onChange={handleSearchModeChange}
              style={{ marginRight: 12, minWidth: 180, width: 'auto' }}
            />
          </div>

          {searchMode === 'quick' ? (
            <>
              <div className="advanced-search-panel">
                <Row gutter={[16, 16]} align="middle">
                  {/* First Row - Three Main Filters */}
                  <Col xs={24} md={8}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Typography.Text strong className="filter-label">
                        <EnvironmentOutlined style={{ marginRight: '8px' }} />
                        State
                      </Typography.Text>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <Button
                          size="large"
                            style={{ 
                              width: '100%', 
                              textAlign: 'left',
                              height: '48px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              borderRadius: '8px',
                              border: '1px solid #d9d9d9',
                              background: '#fff'
                            }}
                          onClick={handleStateModalOpen}
                        >
                            <span>{Array.isArray(selectedState) ? (selectedState.length > 0 ? selectedState.join(', ') : 'Select State') : (selectedState || 'Select State')}</span>
                            <EnvironmentOutlined />
                        </Button>
                        {selectedState && (
                          <Button
                            size="large"
                            icon={<CloseOutlined />}
                            onClick={handleClearState}
                              style={{ 
                                height: '48px',
                                width: '48px',
                                borderRadius: '8px'
                              }}
                          />
                        )}
                      </div>
                    </Space>
                  </Col>
                  <Col xs={24} md={8}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Typography.Text strong className="filter-label">
                        <ClockCircleOutlined style={{ marginRight: '8px' }} />
                        Lead Age
                      </Typography.Text>
                      <RangePicker
                        size="large"
                          style={{ 
                            width: '100%', 
                            height: '48px',
                            borderRadius: '8px'
                          }}
                        onChange={handleDateRangeChange}
                        value={dateRange}
                        placeholder={['Start Date', 'End Date']}
                        allowClear
                          suffixIcon={<CalendarOutlined />}
                      />
                    </Space>
                  </Col>
                  <Col xs={24} md={8}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Typography.Text strong className="filter-label">
                        <CalendarOutlined style={{ marginRight: '8px' }} />
                        Birth Year
                      </Typography.Text>
                      <Select
                        size="large"
                        style={{ width: '100%' }}
                          placeholder="Select birth year"
                        value={birthYear}
                        onChange={setBirthYear}
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          option.children.toString().indexOf(input) >= 0
                        }
                          suffixIcon={<CalendarOutlined />}
                      >
                        {Array.from({ length: 100 }, (_, i) => {
                          const year = new Date().getFullYear() - i;
                          return (
                            <Option key={year} value={year}>
                              {year}
                            </Option>
                          );
                        })}
                      </Select>
                    </Space>
                  </Col>

                  {/* Second Row - Additional Filters */}
                  <Col xs={24} md={8}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Typography.Text strong className="filter-label">
                        <DollarOutlined style={{ marginRight: '8px' }} />
                        Coverage Amount
                      </Typography.Text>
                      <Select
                        size="large"
                        style={{ width: '100%' }}
                          placeholder="Select coverage"
                        value={coverageAmount}
                        onChange={setCoverageAmount}
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                          suffixIcon={<DollarOutlined />}
                      >
                        {coverageRanges.map(range => (
                          <Option key={range.value} value={range.value}>
                            {range.label}
                          </Option>
                        ))}
                      </Select>
                    </Space>
                  </Col>
                  <Col xs={24} md={8}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Typography.Text strong className="filter-label">
                        <InsuranceOutlined style={{ marginRight: '8px' }} />
                        Insurance Type
                      </Typography.Text>
                      <Select
                        size="large"
                        style={{ width: '100%' }}
                          placeholder="Select type"
                        value={insuranceType}
                        onChange={setInsuranceType}
                        allowClear
                          suffixIcon={<InsuranceOutlined />}
                      >
                        <Option value="Permanent Life Insurance">Permanent Life Insurance</Option>
                        <Option value="Final Expense">Final Expense</Option>
                        <Option value="Mortgage Protection">Mortgage Protection</Option>
                      </Select>
                    </Space>
                  </Col>
                  <Col xs={24} md={8}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Typography.Text strong className="filter-label">
                        &nbsp;
                      </Typography.Text>
                    <Button 
                      type="default" 
                      size="large"
                      icon={<SearchOutlined />}
                      onClick={() => {
                        setSelectedState(null);
                        setDateRange(null);
                        setBirthYear(null);
                        setCoverageAmount(null);
                        setInsuranceType(null);
                        localStorage.setItem('selectedState', 'null');
                      }}
                          style={{ 
                            width: '100%',
                            height: '48px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            borderRadius: '8px'
                      }}
                    >
                      Reset Filters
                    </Button>
                    </Space>
                  </Col>
                </Row>
              </div>
              {/* Active Filters */}
              <div className="filter-tags-container" style={{ marginTop: 16 }}>
                {selectedState && (
                  <div className="filter-tag filter-tag-state">
                    <EnvironmentOutlined />
                    <Typography.Text strong>
                      State: {Array.isArray(selectedState) ? selectedState.join(', ') : selectedState}
                    </Typography.Text>
                    <Button 
                      type="text" 
                      icon={<CloseOutlined />} 
                      onClick={handleClearState}
                      className="filter-tag-close"
                    />
                  </div>
                )}

                {birthYear && (
                  <div className="filter-tag filter-tag-year">
                    <CalendarOutlined />
                    <Typography.Text strong>
                      Year: {Array.isArray(birthYear) ? birthYear.join(', ') : birthYear}
                    </Typography.Text>
                    <Button 
                      type="text" 
                      icon={<CloseOutlined />} 
                      onClick={() => setBirthYear(null)}
                      className="filter-tag-close"
                    />
                  </div>
                )}

                {coverageAmount && (
                  <div className="filter-tag filter-tag-coverage">
                    <DollarOutlined />
                    <Typography.Text strong>
                      Coverage: {coverageRanges.find(r => r.value === coverageAmount)?.label}
                    </Typography.Text>
                    <Button 
                      type="text" 
                      icon={<CloseOutlined />} 
                      onClick={() => setCoverageAmount(null)}
                      className="filter-tag-close"
                              />
                            </div>
                )}

                {insuranceType && (
                  <div className="filter-tag filter-tag-insurance">
                    <InsuranceOutlined />
                    <Typography.Text strong>
                      Insurance: {insuranceType}
                    </Typography.Text>
                    <Button 
                      type="text" 
                      icon={<CloseOutlined />} 
                      onClick={() => setInsuranceType(null)}
                      className="filter-tag-close"
                    />
                  </div>
                )}

                {dateRange && dateRange[0] && dateRange[1] && (
                  <div className="filter-tag filter-tag-date">
                    <ClockCircleOutlined />
                    <Typography.Text strong>
                      Date: {dateRange[0].format('MMM D')} - {dateRange[1].format('MMM D')}
                    </Typography.Text>
                    <Button 
                      type="text" 
                      icon={<CloseOutlined />} 
                      onClick={() => setDateRange(null)}
                      className="filter-tag-close"
                    />
                  </div>
                )}
              </div>
            </>
          ) : (
            <div style={{ 
              textAlign: 'center', 
              padding: '24px 16px',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
              borderRadius: '12px',
              margin: '16px 0',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: '1px solid rgba(0,0,0,0.06)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Background decorative elements */}
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '200px',
                height: '200px',
                background: 'radial-gradient(circle at top right, rgba(24,144,255,0.08) 0%, rgba(24,144,255,0) 70%)',
                pointerEvents: 'none'
              }} />
              <div style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: '150px',
                height: '150px',
                background: 'radial-gradient(circle at bottom left, rgba(24,144,255,0.06) 0%, rgba(24,144,255,0) 70%)',
                pointerEvents: 'none'
              }} />

              <div style={{ 
                marginBottom: '16px',
                position: 'relative',
                maxWidth: '800px',
                margin: '0 auto 16px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(24,144,255,0.25)',
                  border: '2px solid #fff',
                  marginBottom: '8px'
                }}>
                  <SearchOutlined style={{ fontSize: '20px', color: '#fff' }} />
                </div>
                <Typography.Title level={2} style={{ 
                  marginTop: 0,
                  marginBottom: '8px',
                  background: 'linear-gradient(135deg, #1f1f1f 0%, #434343 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontSize: '20px',
                  textAlign: 'center'
                }}>
                  Guided Multi Search Experience
                </Typography.Title>
                <Typography.Text type="secondary" style={{ 
                  fontSize: '14px',
                  maxWidth: '600px',
                  margin: '0 auto',
                  display: 'block',
                  lineHeight: '1.4',
                  color: '#666'
                }}>
                  Let us help you find the perfect leads through multi-step guided search
                </Typography.Text>
              </div>

              {/* Show selected filters if any */}
              {(selectedState || dateRange || birthYear || coverageAmount || insuranceType) && (
                <div style={{ 
                  marginTop: '16px',
                  padding: '16px',
                  background: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #f0f0f0'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <Typography.Text strong>
                      Current Filters
                    </Typography.Text>
                    <Button 
                      type="link" 
                      danger
                      icon={<DeleteOutlined />}
                      onClick={handleClearAllFilters}
                      style={{ padding: 0 }}
                    >
                      Clear All
                    </Button>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {selectedState && (
                      <div className="filter-tag filter-tag-state">
                        <EnvironmentOutlined />
                        <Typography.Text strong>
                          States: {Array.isArray(selectedState) ? selectedState.join(', ') : selectedState}
                        </Typography.Text>
                        <Button 
                          type="text" 
                          icon={<CloseOutlined />} 
                          onClick={() => handleRemoveFilter('state', selectedState)}
                          className="filter-tag-close"
                        />
                      </div>
                    )}
                    {dateRange && dateRange[0] && dateRange[1] && (
                      <div className="filter-tag filter-tag-date">
                        <ClockCircleOutlined />
                        <Typography.Text strong>
                          Date: {dateRange[0].format('MMM D')} - {dateRange[1].format('MMM D')}
                        </Typography.Text>
                        <Button 
                          type="text" 
                          icon={<CloseOutlined />} 
                          onClick={() => handleRemoveFilter('date')}
                          className="filter-tag-close"
                        />
                      </div>
                    )}
                    {birthYear && (
                      <div className="filter-tag filter-tag-year">
                        <CalendarOutlined />
                        <Typography.Text strong>
                          Years: {Array.isArray(birthYear) ? birthYear.join(', ') : birthYear}
                        </Typography.Text>
                        <Button 
                          type="text" 
                          icon={<CloseOutlined />} 
                          onClick={() => handleRemoveFilter('year', birthYear)}
                          className="filter-tag-close"
                        />
                      </div>
                    )}
                    {coverageAmount && (
                      <div className="filter-tag filter-tag-coverage">
                        <DollarOutlined />
                        <Typography.Text strong>
                          Coverage: {Array.isArray(coverageAmount) 
                            ? coverageAmount.map(value => coverageRanges.find(r => r.value === value)?.label).join(', ')
                            : coverageRanges.find(r => r.value === coverageAmount)?.label}
                        </Typography.Text>
                        <Button 
                          type="text" 
                          icon={<CloseOutlined />} 
                          onClick={() => handleRemoveFilter('coverage', coverageAmount)}
                          className="filter-tag-close"
                        />
                      </div>
                    )}
                    {insuranceType && (
                      <div className="filter-tag filter-tag-insurance">
                        <InsuranceOutlined />
                        <Typography.Text strong>
                          Insurance: {Array.isArray(insuranceType) ? insuranceType.join(', ') : insuranceType}
                        </Typography.Text>
                        <Button 
                          type="text" 
                          icon={<CloseOutlined />} 
                          onClick={() => handleRemoveFilter('insurance', insuranceType)}
                          className="filter-tag-close"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              <Button
                type="primary"
                size="large"
                icon={<SearchOutlined />}
                onClick={() => setSearchWizardVisible(true)}
                style={{
                  height: '40px',
                  padding: '0 24px',
                  fontSize: '14px',
                  borderRadius: '6px',
                  background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(24,144,255,0.25)',
                  transition: 'all 0.3s ease',
                  marginBottom: '16px'
                }}
              >
                Start Search Wizard
              </Button>

              <div style={{ 
                maxWidth: '100%',
                margin: '0 auto',
                padding: '12px',
                background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
              }}>
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  overflowX: 'auto',
                  padding: '4px',
                  scrollbarWidth: 'thin',
                  msOverflowStyle: 'none',
                  '&::-webkit-scrollbar': {
                    display: 'none'
                  }
                }}>
                  {[
                    { 
                      icon: <EnvironmentOutlined />, 
                      title: 'Select your target location', 
                      description: 'Choose the state where you want to find leads',
                      color: '#1890ff'
                    },
                    { 
                      icon: <ClockCircleOutlined />, 
                      title: 'Choose your time range', 
                      description: 'Filter leads by their creation date',
                      color: '#52c41a'
                    },
                    { 
                      icon: <CalendarOutlined />, 
                      title: 'Filter by birth year', 
                      description: 'Find leads within specific age ranges',
                      color: '#faad14'
                    },
                    { 
                      icon: <DollarOutlined />, 
                      title: 'Set coverage amount', 
                      description: 'Specify the desired insurance coverage range',
                      color: '#722ed1'
                    },
                    { 
                      icon: <InsuranceOutlined />, 
                      title: 'Choose insurance type', 
                      description: 'Select the type of insurance the lead is interested in',
                      color: '#eb2f96'
                    }
                  ].map((step, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      padding: '12px',
                      background: 'white',
                      borderRadius: '8px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      border: '1px solid rgba(0,0,0,0.04)',
                      minWidth: '280px',
                      flex: '0 0 auto',
                      ':hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                      }
                    }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        background: `linear-gradient(135deg, ${step.color} 0%, ${step.color}99 100%)`,
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        boxShadow: `0 2px 8px ${step.color}33`
                      }}>
                        {React.cloneElement(step.icon, { style: { fontSize: '16px', color: '#fff' } })}
                      </div>
                      <div style={{ textAlign: 'left', flex: 1 }}>
                        <Typography.Title level={4} style={{ 
                          margin: '0 0 4px 0', 
                          fontSize: '14px',
                          color: '#1f1f1f'
                        }}>
                          {step.title}
                        </Typography.Title>
                        <Typography.Text type="secondary" style={{ 
                          fontSize: '12px',
                          color: '#666',
                          lineHeight: '1.4'
                        }}>
                          {step.description}
                        </Typography.Text>
                      </div>
                      <div style={{
                        width: '20px',
                        height: '20px',
                        background: '#f5f5f5',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#999',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        {index + 1}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Add the Search Wizard Modal */}
        <Modal
          title={
            <div style={{ textAlign: 'center' }}>
              <Typography.Title level={3} style={{ margin: 0 }}>Search Leads</Typography.Title>
            </div>
          }
          open={searchWizardVisible}
          onCancel={handleWizardCancel}
          width={800}
          footer={[
            <Button 
              key="cancel" 
              onClick={handleWizardCancel}
              style={{
                height: '40px',
                padding: '0 20px',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            >
              Cancel
            </Button>,
            <Button
              key="prev"
              onClick={handleWizardPrev}
              disabled={currentStep === 0}
              style={{
                height: '40px',
                padding: '0 20px',
                borderRadius: '8px',
                fontSize: '14px',
                marginLeft: 8
              }}
            >
              Previous
            </Button>,
            <Button
              key="done"
              onClick={handleWizardFinish}
              style={{
                height: '40px',
                padding: '0 20px',
                borderRadius: '8px',
                fontSize: '14px',
                marginLeft: 8,
                background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                border: 'none',
                color: '#fff'
              }}
            >
              Done
            </Button>,
            currentStep < wizardSteps.length - 1 && (
            <Button
              key="next"
              onClick={handleWizardNext}
              style={{
                height: '40px',
                padding: '0 20px',
                borderRadius: '8px',
                fontSize: '14px',
                marginLeft: 8,
                background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                border: 'none',
                color: '#fff'
              }}
            >
              Next
            </Button>
            )
          ].filter(Boolean)}
        >
          <Steps
            current={currentStep}
            items={wizardSteps}
            style={{ marginBottom: '24px' }}
          />
          {/* Active Filters Section */}
          <div style={{ 
            marginBottom: '24px',
            padding: '16px',
            background: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #f0f0f0'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <Typography.Text strong>
                Active Filters
              </Typography.Text>
              {(tempFilters.states.length > 0 || 
                tempFilters.dateRange || 
                tempFilters.birthYears.length > 0 || 
                tempFilters.coverageAmounts.length > 0 || 
                tempFilters.insuranceTypes.length > 0) && (
                <Button 
                  type="link" 
                  danger
                  icon={<DeleteOutlined />}
                  onClick={handleClearAllTempFilters}
                  style={{ padding: 0 }}
                >
                  Clear All
                </Button>
              )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {tempFilters.states.length > 0 && (
                <div className="filter-tag filter-tag-state">
                  <EnvironmentOutlined />
                  <Typography.Text strong>
                    States: {tempFilters.states.join(', ')}
                  </Typography.Text>
                  <Button 
                    type="text" 
                    icon={<CloseOutlined />} 
                    onClick={() => handleRemoveTempFilter('state', tempFilters.states)}
                    className="filter-tag-close"
                  />
                </div>
              )}
              {tempFilters.dateRange && tempFilters.dateRange[0] && tempFilters.dateRange[1] && (
                <div className="filter-tag filter-tag-date">
                  <ClockCircleOutlined />
                  <Typography.Text strong>
                    Date: {tempFilters.dateRange[0].format('MMM D')} - {tempFilters.dateRange[1].format('MMM D')}
                  </Typography.Text>
                  <Button 
                    type="text" 
                    icon={<CloseOutlined />} 
                    onClick={() => handleRemoveTempFilter('date')}
                    className="filter-tag-close"
                  />
                </div>
              )}
              {tempFilters.birthYears.length > 0 && (
                <div className="filter-tag filter-tag-year">
                  <CalendarOutlined />
                  <Typography.Text strong>
                    Years: {tempFilters.birthYears.join(', ')}
                  </Typography.Text>
                  <Button 
                    type="text" 
                    icon={<CloseOutlined />} 
                    onClick={() => handleRemoveTempFilter('year', tempFilters.birthYears)}
                    className="filter-tag-close"
                  />
                </div>
              )}
              {tempFilters.coverageAmounts.length > 0 && (
                <div className="filter-tag filter-tag-coverage">
                  <DollarOutlined />
                  <Typography.Text strong>
                    Coverage: {tempFilters.coverageAmounts.map(value => 
                      coverageRanges.find(r => r.value === value)?.label
                    ).join(', ')}
                  </Typography.Text>
                  <Button 
                    type="text" 
                    icon={<CloseOutlined />} 
                    onClick={() => handleRemoveTempFilter('coverage', tempFilters.coverageAmounts)}
                    className="filter-tag-close"
                  />
                </div>
              )}
              {tempFilters.insuranceTypes.length > 0 && (
                <div className="filter-tag filter-tag-insurance">
                  <InsuranceOutlined />
                  <Typography.Text strong>
                    Insurance: {tempFilters.insuranceTypes.join(', ')}
                  </Typography.Text>
                  <Button 
                    type="text" 
                    icon={<CloseOutlined />} 
                    onClick={() => handleRemoveTempFilter('insurance', tempFilters.insuranceTypes)}
                    className="filter-tag-close"
                  />
                </div>
              )}
              {!tempFilters.states.length && 
               !tempFilters.dateRange && 
               !tempFilters.birthYears.length && 
               !tempFilters.coverageAmounts.length && 
               !tempFilters.insuranceTypes.length && (
                <Typography.Text type="secondary">
                  No filters selected yet
                </Typography.Text>
              )}
            </div>
          </div>
          {renderWizardStep()}
        </Modal>

        {/* Results Card */}
        <Card 
          className="shadow-sm"
          ref={ref4}
          style={{
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            pointerEvents: isTourActive ? 'none' : 'auto',
            opacity: isTourActive ? 0.7 : 1
          }}
        >
          <div className="table-controls-responsive" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12 }}>
            <Segmented
              options={[
                { label: 'Comfortable', value: 'large' },
                { label: 'Normal', value: 'middle' },
                { label: 'Compact', value: 'small' }
              ]}
              value={tableSize}
              onChange={handleTableSizeChange}
              style={{ marginRight: 12, minWidth: 120, width: 'auto' }}
            />
            <Button 
              ref={ref6} 
              icon={<SettingOutlined />} 
              onClick={() => setCustomizeModalOpen(true)}
              disabled={isTourActive}
              style={{ width: '100%', maxWidth: 220 }}
            >
              Customize Columns
            </Button>
          </div>
          <Table
            className={`table-size-${tableSize}`}
            scroll={{ x: 2000 }}
            columns={filteredColumns}
            dataSource={leads?.data || []}
            loading={isLoading}
            rowKey="id"
            pagination={{
              current: leads?.meta?.current_page || 1,
              pageSize: pagination.pageSize,
              total: leads?.meta?.total || 0,
              showSizeChanger: true,
              pageSizeOptions: ['10', '15', '20', '50', '100'],
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              onChange: (page, pageSize) => {
                setPagination({ current: page, pageSize });
              },
              onShowSizeChange: (current, size) => {
                setPagination({ current: 1, pageSize: size });
              }
            }}
            onChange={handleTableChange}
            locale={{
              emptyText: (
                <div className="table-empty-text">
                  <Typography.Text type="secondary">
                    No leads found
                  </Typography.Text>
                </div>
              )
            }}
            rowClassName={record => {
              if (record && !record.availability_status) return 'row-unavailable';
              if (record && record.is_public == false) return 'row-private';
              return '';
            }}
            expandable={{
              expandedRowRender,
              destroyOnClose: false
            }}
          />
        </Card>

        <PurchaseModal
          visible={isPurchaseModalVisible}
          onClose={() => {
            setIsPurchaseModalVisible(false);
            setProcessingLeadId(null);
            setSelectedLead(null);
          }}
          record={selectedLead}
          onPurchase={handleConfirmPurchase}
          isLoading={purchaseMutation.isLoading}
        />

        {/* State Selection Modal */}
        <Modal
          title="Select State"
          open={isStateModalVisible}
          onCancel={handleStateModalClose}
          footer={null}
          width={800}
        >
          <div style={{ marginBottom: 16 }}>
            <Input
              size="large"
              placeholder="Search states..."
              value={stateSearchText}
              onChange={(e) => setStateSearchText(e.target.value)}
              prefix={<SearchOutlined />}
            />
          </div>
          <div style={{ 
            maxHeight: '400px', 
            overflowY: 'auto',
            padding: '16px',
            backgroundColor: '#fafafa',
            borderRadius: '8px'
          }}>
            <Row gutter={[8, 8]}>
              {filteredStates.map(state => (
                <Col xs={12} sm={8} md={6} key={state.id}>
                  <div
                    style={{
                      padding: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      borderRadius: '4px',
                      border: '1px solid #f0f0f0',
                      backgroundColor: selectedState === state.name ? '#e6f7ff' : 'white',
                      boxShadow: selectedState === state.name ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
                      ':hover': {
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }
                    }}
                    onClick={() => handleStateSelect(state.name)}
                  >
                    <Tag
                      color={selectedState === state.name ? 'blue' : 'default'}
                      style={{
                        width: '100%',
                        textAlign: 'center',
                        padding: '8px',
                        margin: 0,
                        fontSize: '14px',
                        borderRadius: '4px',
                        border: 'none',
                        backgroundColor: selectedState === state.name ? '#e6f7ff' : '#f5f5f5',
                        color: selectedState === state.name ? '#1890ff' : 'rgba(0, 0, 0, 0.85)'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>{state.name}</span>
                        <Badge 
                          count={state.available_leads_count} 
                          style={{ 
                            backgroundColor: '#52c41a',
                            marginLeft: '8px'
                          }} 
                        />
                      </div>
                    </Tag>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
          <div style={{ 
            marginTop: 16, 
            textAlign: 'right',
            borderTop: '1px solid #f0f0f0',
            paddingTop: '16px'
          }}>
            <Button onClick={handleStateModalClose}>Cancel</Button>
            <Button 
              type="primary" 
              onClick={handleStateModalClose}
              style={{ marginLeft: 8 }}
            >
              Done
            </Button>
          </div>
        </Modal>

        {/* Fixed Cart Button */}
        {cart.length > 0 && (
          <div ref={ref5} style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1000,
            display: 'flex',
            gap: '8px',
            pointerEvents: isTourActive ? 'none' : 'auto',
            opacity: isTourActive ? 0.7 : 1
          }}>
            <Button
              type="primary"
              size="large"
              icon={<ShoppingCartOutlined />}
              onClick={() => setIsCartVisible(true)}
              style={{
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                height: '48px',
                padding: '0 24px',
                borderRadius: '24px'
              }}
            >
              View Cart ({cart.length})
            </Button>
          </div>
        )}

        <Cart visible={isCartVisible} onClose={() => setIsCartVisible(false)} />

        <Modal
          title="Customize Columns"
          open={customizeModalOpen}
          onCancel={() => setCustomizeModalOpen(false)}
          footer={null}
          bodyStyle={{ padding: 24 }}
          width={600}
        >
          <div style={{ marginBottom: 12 }}>
            <Input
              placeholder="Search columns..."
              value={columnSearch}
              onChange={e => setColumnSearch(e.target.value)}
              allowClear
              size="large"
            />
          </div>
          <Divider style={{ margin: '12px 0' }} />
          <div className="customize-columns-grid" style={{
            maxHeight: 320,
            overflowY: 'auto',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 8,
            marginBottom: 16,
          }}>
            {filteredColumnOptions.map(col => (
              <Checkbox
                key={col.key || col.dataIndex}
                checked={isVisible(col.key || col.dataIndex)}
                onChange={e => setColumnVisibility(col.key || col.dataIndex, e.target.checked)}
                style={{ fontWeight: 400 }}
              >
                {col.title}
              </Checkbox>
            ))}
          </div>
          <Divider style={{ margin: '12px 0' }} />
          <Button type="default" block onClick={resetColumns} style={{ fontWeight: 400 }}>
            Reset to Default
          </Button>
        </Modal>

        <Tour
          open={tourOpen}
          onClose={handleTourEnd}
          steps={tourSteps}
          type="primary"
        />

        <PurchasersModal
          visible={isPurchasersModalVisible}
          onClose={() => setIsPurchasersModalVisible(false)}
          purchasers={selectedLeadPurchasers}
          lead={selectedLead} // Add this line to pass the lead record
        />

        <UploadErrorModal
          visible={uploadErrorModalVisible}
          onClose={() => {
            setUploadErrorModalVisible(false);
            setUploadSummary(null);
          }}
          errors={uploadErrors}
          uploadSummary={uploadSummary}
        />

        {/* CSV Preview Modal */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FileTextOutlined style={{ color: '#1890ff' }} />
              <span>CSV Preview & Validation</span>
              {csvPreviewData.length > 0 && (
                <Tag color="blue" style={{ marginLeft: 'auto' }}>
                  {csvPreviewData.length} records
                </Tag>
              )}
            </div>
          }
          open={csvPreviewVisible}
          onCancel={() => {
            setCsvPreviewVisible(false);
            setCsvPreviewData([]);
            setCsvValidationErrors([]);
            setCsvSearchText('');
            setDuplicateRecords([]);
          }}
          width={1200}
          footer={[
            <Button
              key="cancel"
              onClick={() => {
                setCsvPreviewVisible(false);
                setCsvPreviewData([]);
                setCsvValidationErrors([]);
                setCsvSearchText('');
                setDuplicateRecords([]);
              }}
            >
              Cancel
            </Button>,
            <Button
              key="upload"
              type="primary"
              icon={<UploadOutlined />}
              loading={isUploading}
              disabled={(csvValidationErrors.length > 0 || duplicateRecords.length > 0) || csvPreviewData.length === 0}
              onClick={handleUpload}
              style={{
                background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                border: 'none'
              }}
            >
              {isUploading ? 'Uploading...' : 'Upload CSV'}
            </Button>
          ]}
        >
          <div style={{ marginBottom: 16 }}>
            <Alert
              message={
                csvValidationErrors.length > 0 || duplicateRecords.length > 0
                  ? `Found ${csvValidationErrors.length} validation error(s) and ${duplicateRecords.length} duplicate group(s). Please fix them before uploading.`
                  : `All ${csvPreviewData.length} records are valid and ready to upload.`
              }
              type={csvValidationErrors.length > 0 || duplicateRecords.length > 0 ? 'warning' : 'success'}
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={6}>
                <Card size="small" style={{ textAlign: 'center' }}>
                  <Statistic
                    title="Total Records"
                    value={csvPreviewData.length}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small" style={{ textAlign: 'center' }}>
                  <Statistic
                    title="Valid Records"
                    value={csvPreviewData.length - csvValidationErrors.length}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small" style={{ textAlign: 'center' }}>
                  <Statistic
                    title="Invalid Records"
                    value={csvValidationErrors.length}
                    valueStyle={{ color: '#ff4d4f' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small" style={{ textAlign: 'center' }}>
                  <Statistic
                    title="Duplicates"
                    value={duplicateRecords.length}
                    valueStyle={{ color: '#faad14' }}
                  />
                </Card>
              </Col>
            </Row>
          </div>

          {/* Duplicates Section */}
          {duplicateRecords.length > 0 && (
            <Card
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <ExclamationCircleOutlined style={{ color: '#faad14' }} />
                  <span>Duplicate Records</span>
                  <Tag color="orange" style={{ marginLeft: 'auto' }}>
                    {duplicateRecords.length} duplicate groups
                  </Tag>
                </div>
              }
              style={{ 
                marginBottom: 16,
                border: '1px solid #ffe58f',
                background: '#fffbe6'
              }}
            >
              <Table
                dataSource={duplicateRecords}
                columns={[
                  {
                    title: 'Type',
                    dataIndex: 'type',
                    key: 'type',
                    width: 100,
                    render: (type) => (
                      <Tag color={type === 'identifier' ? 'blue' : type === 'email' ? 'green' : 'purple'}>
                        {type.toUpperCase()}
                      </Tag>
                    )
                  },
                  {
                    title: 'Value',
                    dataIndex: 'value',
                    key: 'value',
                    width: 200,
                    render: (value) => (
                      <Typography.Text copyable style={{ fontSize: '12px' }}>
                        {value || '-'}
                      </Typography.Text>
                    )
                  },
                  {
                    title: 'Duplicate Rows',
                    dataIndex: 'rows',
                    key: 'rows',
                    render: (rows) => (
                      <div>
                        {rows.map((row, idx) => (
                          <Tag key={idx} color="red" style={{ marginRight: 4, marginBottom: 4 }}>
                            Row {row}
                          </Tag>
                        ))}
                      </div>
                    )
                  },
                  {
                    title: 'Count',
                    dataIndex: 'count',
                    key: 'count',
                    width: 80,
                    render: (count) => (
                      <Badge count={count} style={{ backgroundColor: '#ff4d4f' }} />
                    )
                  }
                ]}
                rowKey={(record, index) => `duplicate-${index}`}
                pagination={false}
                size="small"
                scroll={{ y: 200 }}
              />
            </Card>
          )}

          <Row gutter={[16, 16]}>
            <Col xs={24} lg={csvValidationErrors.length > 0 || duplicateRecords.length > 0 ? 16 : 24}>
              <Card
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    <span>CSV Data Preview</span>
                    <div style={{ marginLeft: 'auto', flex: 1, maxWidth: 300 }}>
                      <Input
                        placeholder="Search in preview..."
                        prefix={<SearchOutlined />}
                        value={csvSearchText}
                        onChange={(e) => setCsvSearchText(e.target.value)}
                        allowClear
                        size="small"
                      />
                    </div>
                  </div>
                }
                style={{ marginBottom: 16 }}
              >
                <Table
                  dataSource={csvPreviewData.filter(row => {
                    if (!csvSearchText) return true;
                    const searchLower = csvSearchText.toLowerCase();
                    return Object.values(row).some(value => 
                      String(value).toLowerCase().includes(searchLower)
                    );
                  })}
                  columns={csvPreviewColumns}
                  rowKey={(record, index) => `preview-row-${index}`}
                  pagination={{ pageSize: 10, showTotal: (total, range) => 
                    csvSearchText 
                      ? `${range[0]}-${range[1]} of ${total} filtered records` 
                      : `${range[0]}-${range[1]} of ${total} records`
                  }}
                  size="small"
                  scroll={{ x: 800, y: 400 }}
                  rowClassName={(record, index) => {
                    const rowNumber = index + 2;
                    const isDuplicate = duplicateRecords.some(dup => dup.rows.includes(rowNumber));
                    const hasError = csvValidationErrors.some(err => err.row === rowNumber);
                    if (isDuplicate) return 'csv-row-duplicate';
                    if (hasError) return 'csv-row-error';
                    return '';
                  }}
                />
              </Card>
            </Col>

            {csvValidationErrors.length > 0 && (
              <Col xs={24} lg={8}>
                <Card
                  title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
                      <span>Validation Errors</span>
                      <Tag color="red" style={{ marginLeft: 'auto' }}>
                        {csvValidationErrors.length}
                      </Tag>
                    </div>
                  }
                  style={{
                    border: '1px solid #ffccc7',
                    background: '#fff2f0'
                  }}
                >
                  <Table
                    dataSource={csvValidationErrors}
                    columns={csvErrorColumns}
                    rowKey="row"
                    pagination={false}
                    size="small"
                    scroll={{ y: 400 }}
                  />
                </Card>
              </Col>
            )}
          </Row>
        </Modal>
      </div>
    </Elements>
  );
};

// Add this before the final return statement
const PurchasersModal = ({ visible, onClose, purchasers, lead }) => {
  const [selectedPurchaser, setSelectedPurchaser] = React.useState(null);
  const printRef = useRef();

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: 'Lead Purchase Verification',
    onBeforeGetContent: () => {
      return new Promise((resolve) => {
        // Ensure the content is rendered
        setSelectedPurchaser(selectedPurchaser);
        setTimeout(resolve, 100);
      });
    },
    pageStyle: `
      @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
      
      body { 
        font-family: 'Inter', sans-serif; 
        padding: 15px;
        color: #1f1f1f;
        background: #fff;
        line-height: 1.3;
        font-size: 11px;
      }
      
      .header { 
        text-align: center; 
        margin-bottom: 10px;
        padding-bottom: 5px;
        border-bottom: 1px solid #f0f0f0;
      }
      
      .header h1 {
        font-size: 16px;
        font-weight: 700;
        color: #1f1f1f;
        margin-bottom: 2px;
      }
      
      .header p {
        color: #666;
        font-size: 11px;
      }
      
      .section { 
        margin-bottom: 6px;
        background: #fafafa;
        padding: 8px;
        border-radius: 4px;
      }
      
      .section h2 {
        font-size: 12px;
        font-weight: 600;
        color: #1f1f1f;
        margin-bottom: 4px;
        padding-bottom: 3px;
        border-bottom: 1px solid #e8e8e8;
      }
      
      .info-grid { 
        display: grid; 
        grid-template-columns: 1fr 1fr; 
        gap: 6px;
      }
      
      .info-item { 
        margin-bottom: 3px;
        padding: 4px;
        background: #fff;
        border-radius: 3px;
      }
      
      .label { 
        font-weight: 500;
        color: #666;
        font-size: 10px;
        margin-bottom: 1px;
      }
      
      .value { 
        color: #1f1f1f;
        font-size: 11px;
        font-weight: 500;
        word-break: break-all;
      }

      .device-flags {
        display: flex;
        gap: 4px;
        flex-wrap: wrap;
        margin-bottom: 4px;
      }

      .device-flag {
        padding: 1px 6px;
        border-radius: 3px;
        font-size: 9px;
        font-weight: 500;
        color: white;
      }

      .device-flag.mobile { background-color: #1890ff; }
      .device-flag.tablet { background-color: #52c41a; }
      .device-flag.desktop { background-color: #722ed1; }
      .device-flag.robot { background-color: #f5222d; }

      .additional-data {
        margin-top: 4px;
        background: #fff;
        padding: 4px;
        border-radius: 3px;
        font-family: monospace;
        font-size: 9px;
        white-space: pre-wrap;
        word-break: break-all;
        max-height: 60px;
        overflow-y: auto;
      }
      
      .footer { 
        margin-top: 4px;
        text-align: center;
        padding-top: 4px;
        border-top: 1px solid #f0f0f0;
      }
      
      .signature-line {
        margin: 10px auto 5px;
        border-top: 1px solid #000;
        width: 150px;
        padding-top: 3px;
      }
      
      .verification-stamp {
        position: absolute;
        top: 15px;
        right: 15px;
        width: 60px;
        height: 60px;
        border: 2px solid #1890ff;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        transform: rotate(-15deg);
        opacity: 0.8;
      }
      
      .verification-stamp::before {
        content: "VERIFIED";
        font-size: 10px;
        font-weight: 600;
        color: #1890ff;
      }
      
      @media print {
        .no-print { display: none; }
        body { padding: 8px; }
        .section { box-shadow: none; }
        .info-item { box-shadow: none; }
        .additional-data { max-height: none; }
        @page { margin: 0.5cm; }
      }
    `
  });

  return (
    <Modal
      title={
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <Typography.Title level={4} style={{ margin: 0 }}>Lead Purchase History</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: '12px' }}>View and verify lead purchases</Typography.Text>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={900}
      bodyStyle={{ padding: '24px' }}
    >
      <div style={{ 
        padding: '16px 0',
        borderBottom: '1px solid #f0f0f0',
        marginBottom: 24,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography.Title level={5} style={{ margin: 0, fontSize: '14px' }}>
          Total Purchases: {purchasers.length}
        </Typography.Title>
        <Tag color="success" style={{ 
          padding: '2px 8px',
          borderRadius: '8px',
          fontSize: '11px',
          fontWeight: 500
        }}>
          Verified Transactions
        </Tag>
      </div>

      <Row gutter={[16, 16]}>
        {purchasers.map((purchaser) => (
          <Col xs={24} sm={24} md={12} lg={8} key={purchaser.transaction_id}>
            <Card
              hoverable
              style={{
                height: '100%',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}
              bodyStyle={{ padding: '16px' }}
              actions={[
                <Button
                  type="primary"
                  onClick={() => {
                    setSelectedPurchaser(purchaser);
                    setTimeout(() => {
                      handlePrint();
                    }, 100);
                  }}
                  icon={<PrinterOutlined />}
                  size="small"
                  style={{
                    background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                    border: 'none',
                    boxShadow: '0 2px 8px rgba(24,144,255,0.25)'
                  }}
                >
                  Print Verification
                </Button>
              ]}
            >
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography.Title level={5} style={{ margin: 0 }}>
                    {purchaser.name}
                  </Typography.Title>
                  <Tag color="blue" style={{ 
                    padding: '2px 8px',
                    borderRadius: '8px',
                    fontSize: '11px',
                    fontWeight: 500
                  }}>
                    {new Date(purchaser.purchase_date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Tag>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <MailOutlined style={{ color: '#1890ff' }} />
                    <Typography.Text>{purchaser.email}</Typography.Text>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <PhoneOutlined style={{ color: '#1890ff' }} />
                    <Typography.Text>{purchaser.phone}</Typography.Text>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <TagOutlined style={{ color: '#1890ff' }} />
                    <Typography.Text copyable style={{ fontSize: '12px' }}>
                      Transaction: {purchaser.transaction_id}
                    </Typography.Text>
                  </div>
                </div>

                {purchaser.return_status && (
                  <Card 
                    size="small" 
                    style={{ 
                      background: '#fafafa',
                      border: '1px solid #f0f0f0'
                    }}
                  >
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography.Text strong>Return Status</Typography.Text>
                        <Tag color={
                          purchaser.return_status.status === 'approved' ? 'green' :
                          purchaser.return_status.status === 'rejected' ? 'red' : 'orange'
                        }>
                          {purchaser.return_status.status.charAt(0).toUpperCase() + 
                           purchaser.return_status.status.slice(1)}
                        </Tag>
                      </div>
                      <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                        Reason: {purchaser.return_status.reason}
                      </Typography.Text>
                      <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                        Date: {new Date(purchaser.return_status.created_at).toLocaleDateString()}
                      </Typography.Text>
                    </Space>
                  </Card>
                )}
              </Space>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Hidden print content */}
      <div style={{ display: 'none' }}>
        <div ref={printRef}>
          {selectedPurchaser && <PurchaseVerificationPrint purchaser={selectedPurchaser} lead={lead} />}
        </div>
      </div>
    </Modal>
  );
};

// Update the styles
const styles = `
.advanced-search-panel {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
}

.filter-label {
  font-size: 14px;
  color: #1f1f1f;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
}

/* Tour Customization */
.ant-tour-inner {
  background: #000 !important;
  color: #fff !important;
}

.ant-tour-inner .ant-tour-title {
  color: #fff !important;
}

.ant-tour-inner .ant-tour-description {
  color: rgba(255, 255, 255, 0.85) !important;
  white-space: pre-line !important;
}

.ant-tour-inner .ant-tour-next-btn {
  background: #fff !important;
  border-color: #fff !important;
  color: #000 !important;
}

.ant-tour-inner .ant-tour-prev-btn {
  color: #fff !important;
}

.ant-tour-inner .ant-tour-close {
  color: #fff !important;
}

.ant-tour-inner .ant-tour-close:hover {
  color: rgba(255, 255, 255, 0.75) !important;
}

.ant-tour-inner .ant-tour-indicators {
  background: rgba(255, 255, 255, 0.1) !important;
}

.ant-tour-inner .ant-tour-indicator {
  background: rgba(255, 255, 255, 0.3) !important;
}

.ant-tour-inner .ant-tour-indicator-active {
  background: #fff !important;
}

.advanced-search-panel .ant-select-selector,
.advanced-search-panel .ant-picker,
.advanced-search-panel .ant-input,
.advanced-search-panel .ant-btn,
.advanced-search-panel .ant-select,
.advanced-search-panel .ant-picker-input > input {
  min-height: 48px !important;
  font-size: 16px !important;
  border-radius: 8px !important;
}

.advanced-search-panel .ant-select-selection-item,
.advanced-search-panel .ant-select-selection-placeholder {
  font-size: 16px !important;
}

.advanced-search-panel .ant-btn {
  height: 48px !important;
  font-size: 16px !important;
  border-radius: 8px !important;
}

.advanced-search-panel .ant-picker-input > input {
  height: 48px !important;
  font-size: 16px !important;
}

.filter-tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
}

.filter-tag {
  display: flex;
  align-items: center;
  padding: 4px 12px;
  background: #f5f5f5;
  border-radius: 16px;
  gap: 8px;
  transition: all 0.3s ease;
  color: #222 !important;
}

.filter-tag * {
  color: #222 !important;
  opacity: 1 !important;
}

.filter-tag svg {
  color: #222 !important;
  opacity: 1 !important;
}

.filter-tag-close {
  padding: 0;
  margin-left: 4px;
  color: #222 !important;
  opacity: 1 !important;
}

.filter-tag:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.filter-tag-state { background: #e6f7ff; }
.filter-tag-year { background: #fff7e6; }
.filter-tag-coverage { background: #f6ffed; }
.filter-tag-insurance { background: #f9f0ff; }
.filter-tag-date { background: #fff1f0; }

@media (min-width: 768px) {
  .search-panel {
    position: sticky;
    top: 0;
    zIndex: 1000;
  }
}

@media (max-width: 768px) {
  .advanced-search-panel {
    padding: 12px;
  }
  
  .filter-tags-container {
    gap: 6px;
  }
  
  .filter-tag {
    padding: 3px 10px;
    font-size: 12px;
  }

  .ant-card {
    margin-bottom: 16px;
  }

  .ant-space {
    gap: 8px !important;
  }

  .ant-select-selector {
    height: 48px !important;
  }

  .ant-picker {
    width: 100% !important;
  }

  .ant-btn {
    height: 48px !important;
  }
}

.page-container {
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%);
  min-height: 100vh;
  padding: 24px;
}

.ant-card {
  transition: all 0.3s ease;
}

.ant-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0,0,0,0.1);
}

@media (max-width: 576px) {
  .page-container {
    padding: 16px;
  }

  .ant-card {
    border-radius: 8px;
  }

  .ant-space-vertical {
    width: 100%;
  }

  .page-header {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }

  .ant-upload {
    width: 100%;
  }

  .ant-upload button {
    width: 100%;
  }
}

.ant-select-selector,
.ant-picker,
.ant-btn {
  border-radius: 8px !important;
}

.ant-select-selection-placeholder,
.ant-picker-input > input {
  font-size: 14px;
}

.ant-table {
  border-radius: 8px;
  overflow: hidden;
}

.ant-table-thead > tr > th {
  background: #fafafa !important;
  font-weight: 600;
  padding: 16px !important;
}

.ant-table-tbody > tr:hover > td {
  background: #f5f7fa !important;
}

.ant-table-tbody > tr > td {
  padding: 16px !important;
}

.ant-table-pagination {
  margin: 16px !important;
}

.ant-pagination-item {
  border-radius: 4px !important;
}

.ant-pagination-item-active {
  background: #1890ff !important;
  border-color: #1890ff !important;
}

.ant-pagination-item-active a {
  color: #fff !important;
}

.ant-select-dropdown {
  border-radius: 8px !important;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important;
}

.ant-select-item {
  padding: 8px 12px !important;
}

.ant-select-item-option-selected {
  background: #e6f7ff !important;
}

.ant-select-item-option-active {
  background: #f5f7fa !important;
}

.ant-picker-dropdown {
  border-radius: 8px !important;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important;
}

.ant-picker-panel {
  border-radius: 8px !important;
}

.ant-picker-cell {
  padding: 4px !important;
}

.ant-picker-cell-in-view {
  color: #1f1f1f !important;
}

.ant-picker-cell-selected .ant-picker-cell-inner {
  background: #1890ff !important;
}

.ant-picker-cell-today .ant-picker-cell-inner::before {
  border: 1px solid #1890ff !important;
}

.stat-card {
  transition: all 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0,0,0,0.1);
}

@media (max-width: 768px) {
  .stat-card {
    margin-bottom: 16px;
  }
  
  .page-header {
    padding: 16px;
  }
  
  .page-header > div {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .page-header > div > div:last-child {
    margin-top: 16px;
    width: 100%;
  }
  
  .page-header > div > div:last-child button {
    width: 100%;
  }
}

.table-size-large .ant-table-tbody > tr > td, .table-size-large .ant-table-thead > tr > th {
  padding: 20px 24px !important;
  font-size: 18px !important;
}
.table-size-middle .ant-table-tbody > tr > td, .table-size-middle .ant-table-thead > tr > th {
  padding: 16px !important;
  font-size: 16px !important;
}
.table-size-small .ant-table-tbody > tr > td, .table-size-small .ant-table-thead > tr > th {
  padding: 8px 12px !important;
  font-size: 14px !important;
}

/* Sticky table header without vertical scroll */
.ant-table .ant-table-header, .ant-table .ant-table-thead {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #fafafa !important;
}

.ant-tour-active {
  pointer-events: none !important;
  opacity: 0.7 !important;
}

.ant-tour-active * {
  pointer-events: none !important;
}

.ant-tour-active .ant-btn,
.ant-tour-active .ant-select,
.ant-tour-active .ant-picker,
.ant-tour-active .ant-input {
  cursor: not-allowed !important;
}

/* Responsive styles for Advanced Search */
@media (max-width: 768px) {
  .advanced-search-panel {
    padding: 12px;
  }

  .advanced-search-panel .ant-row {
    margin: 0 !important;
  }

  .advanced-search-panel .ant-col {
    padding: 0 !important;
    margin-bottom: 16px;
  }

  .advanced-search-panel .ant-space {
    width: 100%;
  }

  .advanced-search-panel .ant-select,
  .advanced-search-panel .ant-picker,
  .advanced-search-panel .ant-input,
  .advanced-search-panel .ant-btn {
    width: 100% !important;
  }

  .search-panel .ant-card-head {
    padding: 12px !important;
  }

  .search-panel .ant-card-head-title {
    padding: 0 !important;
  }

  .search-panel .ant-segmented {
    width: 100%;
    margin-top: 12px;
  }

  .search-panel .ant-segmented-item {
    flex: 1;
    text-align: center;
  }
}

/* Responsive styles for Customize Columns Modal */
@media (max-width: 768px) {
  .ant-modal {
    max-width: 100% !important;
    margin: 0 !important;
    top: 0 !important;
    padding-bottom: 0 !important;
  }

  .ant-modal-content {
    border-radius: 0 !important;
    height: 100vh !important;
    display: flex !important;
    flex-direction: column !important;
  }

  .ant-modal-body {
    flex: 1 !important;
    overflow-y: auto !important;
    padding: 16px !important;
  }

  .ant-modal-header {
    padding: 16px !important;
  }

  .ant-modal-footer {
    padding: 16px !important;
    border-top: 1px solid #f0f0f0 !important;
  }

  .customize-columns-grid {
    display: grid !important;
    grid-template-columns: 1fr !important;
    gap: 8px !important;
  }

  .customize-columns-search {
    margin-bottom: 16px !important;
  }

  .customize-columns-search .ant-input {
    height: 40px !important;
  }

  .customize-columns-reset {
    margin-top: 16px !important;
  }
}

/* Additional responsive improvements */
@media (max-width: 576px) {
  .page-container {
    padding: 12px !important;
  }

  .ant-card {
    margin-bottom: 12px !important;
  }

  .ant-table {
    font-size: 14px !important;
  }

  .ant-table-thead > tr > th,
  .ant-table-tbody > tr > td {
    padding: 8px !important;
  }

  .ant-pagination {
    margin: 12px 0 !important;
  }

  .ant-pagination-item,
  .ant-pagination-prev,
  .ant-pagination-next {
    min-width: 32px !important;
    height: 32px !important;
    line-height: 32px !important;
  }
}

/* Improve modal responsiveness */
.ant-modal {
  max-width: 90vw !important;
}

@media (max-width: 768px) {
  .ant-modal {
    max-width: 100vw !important;
    margin: 0 !important;
    top: 0 !important;
    padding-bottom: 0 !important;
  }
}

/* Improve search panel responsiveness */
.search-panel {
  position: relative !important;
  z-index: 1000 !important;
}

@media (max-width: 768px) {
  .search-panel {
    position: relative !important;
    top: 0 !important;
  }
}

/* Improve filter tags responsiveness */
.filter-tags-container {
  display: flex !important;
  flex-wrap: wrap !important;
  gap: 8px !important;
  margin-top: 16px !important;
}

@media (max-width: 576px) {
  .filter-tags-container {
    gap: 6px !important;
  }

  .filter-tag {
    padding: 4px 8px !important;
    font-size: 12px !important;
  }
}

.search-panel-header-responsive {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

@media (max-width: 768px) {
  .search-panel-header-responsive {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  .search-panel-header-responsive .ant-typography {
    font-size: 1.2rem !important;
    line-height: 1.2 !important;
    margin-bottom: 2px !important;
  }
  .search-panel-header-responsive .ant-typography-secondary {
    font-size: 0.95rem !important;
    line-height: 1.2 !important;
    margin-bottom: 0 !important;
  }
  .search-panel-header-responsive .ant-segmented {
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;
  }
}

.table-controls-responsive {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  gap: 12px;
}

@media (max-width: 768px) {
  .table-controls-responsive {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
  .table-controls-responsive .ant-segmented {
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;
    margin-right: 0 !important;
  }
  .table-controls-responsive .ant-btn {
    width: 100% !important;
    max-width: 100% !important;
  }
}

@media (max-width: 768px) {
  .table-controls-responsive .ant-segmented {
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;
    margin-right: 0 !important;
    display: flex !important;
  }
  .table-controls-responsive .ant-segmented .ant-segmented-item {
    flex: 1 1 0 !important;
    text-align: center !important;
  }
}

.row-private {
  background-color: #fff1f0 !important;
}
.row-private:hover > td {
  background-color: #ffccc7 !important;
}

.csv-row-duplicate {
  background-color: #fffbe6 !important;
}
.csv-row-duplicate:hover > td {
  background-color: #ffe58f !important;
}

.csv-row-error {
  background-color: #fff2f0 !important;
}
.csv-row-error:hover > td {
  background-color: #ffccc7 !important;
}

.error-row-duplicate {
  background-color: #fffbe6 !important;
}
.error-row-duplicate:hover > td {
  background-color: #ffe58f !important;
}

.error-row-validation {
  background-color: #fff2f0 !important;
}
.error-row-validation:hover > td {
  background-color: #ffccc7 !important;
}
`;

// Add the styles to the document
const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export default LeadsPage;